import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';

import { supabase } from '../../lib/supabase';
import { Booking, getBooking } from '../../lib/bookings';
import { clockIn, getTimeclockStatus } from '../../lib/timeclock';
import BookingCustomerInfo from './BookingCustomerInfo';
import { Customer, getCustomer } from '../../lib/customers';
import { getPersonInitials } from '../../utils/personInitials';
import SelectCustomerModal from '../manage/SelectCustomerModal';
import BookingServiceList from './BookingServiceList';
import BookingDocumentsSection from '../documents/BookingDocumentsSection';
import BookingProductList from './BookingProductList';
import ScheduleTimeline from './ScheduleTimeline';
import SchedulePickerPanel from './SchedulePickerPanel';
import BookingFieldQuotesSection from './BookingFieldQuotesSection';
import BookingResourceList from './BookingResourceList';
import BookingPaymentSummary from './BookingPaymentSummary';
import { useBookingInvoice } from '../../hooks/useBookingInvoice';
import { isInvoiceLocked } from '../../lib/invoices/getInvoiceForBooking';
import AmendInvoiceModal from '../invoices/AmendInvoiceModal';
import BookingMaterialsTab from './BookingMaterialsTab';
import BookingFlatRatesTab from './BookingFlatRatesTab';
import { usePermissionCheck } from '../../hooks/usePermissionCheck';
import { useJobTimer } from '../../hooks/useJobTimer';
import { useBookingActiveTimers } from '../../hooks/useBookingActiveTimers';
import { getBookingCommissions } from '../../lib/bookingCommissions';

import { useBusiness } from '../../contexts/BusinessContext';
import { triggerBookingCreatedAfterScheduling } from '../../lib/automationsV2';
import { useAppSystem } from '../../hooks/useAppSystem';

import BookingProfitLoss from './BookingProfitLoss';
import BookingCommissionSection from './BookingCommissionSection';
import { format } from 'date-fns';
import { updateBooking } from '../../lib/bookings';
import ApplyDiscountModal from '../ApplyDiscountModal';
import RecordPaymentModal from '../RecordPaymentModal';
import PaymentDetailsModal from '../PaymentDetailsModal';
import BetaPaymentsModal from '../BetaPaymentsModal';
import { Payment } from '../../lib/payments';
import { paymentPrincipal, isPaymentCountedForBalance } from '../../lib/paymentBreakdown';
import { ExternalLink, Copy, CheckCircle, Star, ChevronRight, ChevronDown, ChevronLeft, XCircle, Clock, Calendar, Circle, Shield, Info, Save, Edit3, Mail, Users, Play, Square, FileText, X, Lock } from 'lucide-react';
import { buildFeedbackCustomerLink, generateFeedbackForBooking, getBookingFeedback, isFeedbackEnabled, updateFeedbackRatings, type BookingFeedback } from '../../lib/feedback';
import { getAllFeedbackFormFields, type FeedbackFormField } from '../../lib/feedbackForm';
import { 
  getLiabilityWaiverByBookingId, 
  sendLiabilityWaiverSMS, 
  sendLiabilityWaiverEmail,
  isLiabilityWaiverEnabled,
  generateLiabilityWaiver,
  generateLiabilityWaiverURL,
  type LiabilityWaiver 
} from '../../lib/liabilityWaivers';
import SendLiabilityWaiverModal from '../SendLiabilityWaiverModal';
import SendInvoiceModal from '../SendInvoiceModal';
import VehicleDamageReportModal from '../VehicleDamageReportModal';
import { sendEmailWithFallback } from '../../lib/email';
import EditableStarRating from './EditableStarRating';
import BookingSeriesLine from './BookingSeriesCard';
import ProjectInfoModal from '../ProjectInfoModal';
import { applyReferralDiscount } from '../../lib/referralProgram';

// Custom portal-based toast component for modal contexts
const PortalToast = ({ 
  children, 
  type, 
  onClose 
}: { 
  children: React.ReactNode; 
  type: 'loading' | 'success' | 'error';
  onClose: () => void;
}) => {
  const getIcon = () => {
    switch (type) {
      case 'loading':
        return <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>;
      case 'success':
        return <div className="rounded-full h-4 w-4 bg-green-500 flex items-center justify-center text-white text-xs">✓</div>;
      case 'error':
        return <div className="rounded-full h-4 w-4 bg-red-500 flex items-center justify-center text-white text-xs">✕</div>;
    }
  };

  const getBorderColor = () => {
    switch (type) {
      case 'loading':
        return 'border-blue-200';
      case 'success':
        return 'border-green-200';
      case 'error':
        return 'border-red-200';
    }
  };

  return createPortal(
    <div 
      className={`fixed left-1/2 transform -translate-x-1/2 bg-white rounded-lg shadow-lg border ${getBorderColor()} p-4 flex items-center space-x-3 min-w-[300px] max-w-md`}
      style={{ zIndex: 999999, top: 'calc(env(safe-area-inset-top, 0px) + 16px)' }}
    >
      {getIcon()}
      <div className="flex-1">
        {children}
      </div>
      {type !== 'loading' && (
        <button 
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 ml-2"
        >
          ×
        </button>
      )}
    </div>,
    document.body
  );
};

// Custom toast function with portal rendering for modal contexts
const createPortalToast = (
  setShowPortalToast: (show: boolean) => void,
  setPortalToastMessage: (message: React.ReactNode) => void,
  setPortalToastType: (type: 'loading' | 'success' | 'error') => void
) => ({
  loading: (message: React.ReactNode, options?: any) => {
    setPortalToastMessage(message);
    setPortalToastType('loading');
    setShowPortalToast(true);
    return 'portal-toast-loading';
  },
  success: (message: string, options?: any) => {
    setPortalToastMessage(message);
    setPortalToastType('success');
    setShowPortalToast(true);
    // Auto-hide success toast after 4 seconds
    setTimeout(() => setShowPortalToast(false), 4000);
    return 'portal-toast-success';
  },
  error: (message: string, options?: any) => {
    setPortalToastMessage(message);
    setPortalToastType('error');
    setShowPortalToast(true);
    // Auto-hide error toast after 4 seconds
    setTimeout(() => setShowPortalToast(false), 4000);
    return 'portal-toast-error';
  },
  dismiss: (toastId?: string) => {
    setShowPortalToast(false);
  }
});

import { 
  getDamageReportByBookingId, 
  hasDamageReport,
  type VehicleDamageReport 
} from '../../lib/damageReports';

interface DynamicFeedbackResponse {
  [fieldKey: string]: string | number;
}

interface ExtendedBookingFeedback extends BookingFeedback {
  form_responses?: DynamicFeedbackResponse;
}

// Time log row for the job time logs modal
interface JobTimeLogEntry {
  id: string;
  resource_id: string;
  started_at: string;
  stopped_at: string | null;
  duration_seconds: number | null;
  start_method: string;
  stop_method: string | null;
  resources?: { id: string; first_name: string | null; last_name: string | null } | null;
}

function JobTimeLogsModal({ bookingId, onClose, isAdmin = false }: { bookingId: string; onClose: () => void; isAdmin?: boolean }) {
  const [entries, setEntries] = useState<JobTimeLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stoppingTimerId, setStoppingTimerId] = useState<string | null>(null);

  const fetchEntries = useCallback(async (showLoader = false) => {
    if (showLoader) setLoading(true);
    setError(null);
    const { data, error: fetchError } = await supabase
      .from('job_time_entries')
      .select(`
        id,
        resource_id,
        started_at,
        stopped_at,
        duration_seconds,
        start_method,
        stop_method,
        resources:resource_id ( id, first_name, last_name )
      `)
      .eq('booking_id', bookingId)
      .order('started_at', { ascending: false });
    if (fetchError) {
      setError(fetchError.message);
      setEntries([]);
    } else {
      setEntries((data || []).map((row: any) => ({
        ...row,
        resources: row.resources ?? row.resource ?? null,
      })));
    }
    setLoading(false);
  }, [bookingId]);

  useEffect(() => {
    fetchEntries(true);
  }, [fetchEntries]);

  // Realtime: update the logs list whenever job_time_entries change
  useEffect(() => {
    const channel = supabase
      .channel(`job-time-logs-modal-${bookingId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'job_time_entries',
          filter: `booking_id=eq.${bookingId}`,
        },
        () => {
          fetchEntries();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [bookingId, fetchEntries]);

  const formatTime = (iso: string) => format(new Date(iso), 'MMM d, h:mm a');
  const formatDuration = (seconds: number | null) => {
    if (seconds == null) return '—';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m`;
    return `${seconds}s`;
  };

  const handleAdminStopTimer = async (resourceId: string, entryId: string) => {
    setStoppingTimerId(entryId);
    try {
      const { error: stopError } = await supabase.rpc('stop_job_timer', {
        p_booking_id: bookingId,
        p_resource_id: resourceId,
        p_method: 'admin',
      });
      if (stopError) throw stopError;
      await fetchEntries();
    } catch (err: any) {
      console.error('Admin stop timer error:', err.message);
    } finally {
      setStoppingTimerId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Time logs</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-blue-600" />
            </div>
          )}
          {error && (
            <p className="text-sm text-red-600 py-4">{error}</p>
          )}
          {!loading && !error && entries.length === 0 && (
            <p className="text-sm text-gray-500 py-4">No time entries for this job yet.</p>
          )}
          {!loading && !error && entries.length > 0 && (
            <div className="space-y-1">
              <div className={`grid gap-2 px-2 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-200 ${isAdmin ? 'grid-cols-[2.5fr_2fr_2fr_1fr_1fr_auto]' : 'grid-cols-12'}`}>
                <div className={isAdmin ? '' : 'col-span-4'}>Resource</div>
                <div className={isAdmin ? '' : 'col-span-3'}>Started</div>
                <div className={isAdmin ? '' : 'col-span-3'}>Stopped</div>
                <div className={`text-right ${isAdmin ? '' : 'col-span-1'}`}>Duration</div>
                <div className={`text-right ${isAdmin ? '' : 'col-span-1'}`}>Method</div>
                {isAdmin && <div className="text-right w-[70px]"></div>}
              </div>
              {entries.map((e) => {
                const name = [e.resources?.first_name, e.resources?.last_name].filter(Boolean).join(' ').trim() || '—';
                const isRunning = !e.stopped_at;
                return (
                  <div
                    key={e.id}
                    className={`grid gap-2 px-2 py-2 text-sm border-b border-gray-100 hover:bg-gray-50/50 last:border-0 items-center ${isAdmin ? 'grid-cols-[2.5fr_2fr_2fr_1fr_1fr_auto]' : 'grid-cols-12'}`}
                  >
                    <div className={`font-medium text-gray-900 truncate ${isAdmin ? '' : 'col-span-4'}`}>{name}</div>
                    <div className={`text-gray-600 ${isAdmin ? '' : 'col-span-3'}`}>{formatTime(e.started_at)}</div>
                    <div className={`text-gray-600 ${isAdmin ? '' : 'col-span-3'}`}>
                      {isRunning ? (
                        <span className="inline-flex items-center gap-1 text-green-600 font-medium">
                          <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                          Running
                        </span>
                      ) : formatTime(e.stopped_at!)}
                    </div>
                    <div className={`text-right font-mono text-gray-600 ${isAdmin ? '' : 'col-span-1'}`}>
                      {e.stopped_at != null && e.duration_seconds != null
                        ? formatDuration(e.duration_seconds)
                        : '—'}
                    </div>
                    <div className={`text-right ${isAdmin ? '' : 'col-span-1'}`}>
                      <span className="text-xs text-gray-400">{e.start_method}</span>
                    </div>
                    {isAdmin && (
                      <div className="text-right w-[70px]">
                        {isRunning && (
                          <button
                            type="button"
                            onClick={() => handleAdminStopTimer(e.resource_id, e.id)}
                            disabled={stoppingTimerId === e.id}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors disabled:opacity-50"
                          >
                            {stoppingTimerId === e.id ? (
                              <div className="animate-spin rounded-full h-3 w-3 border border-red-300 border-t-red-600" />
                            ) : (
                              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                <rect x="6" y="6" width="12" height="12" rx="1" />
                              </svg>
                            )}
                            Stop
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

interface BookingContentProps {
  activeTab: string;
  booking: Booking;
  isSelectMaterialModalOpen: boolean;
  setIsSelectMaterialModalOpen: (isOpen: boolean) => void;
  isExpensesAppInstalled?: boolean;
  onEditCustomer: () => void;
  onChangeCustomer?: () => void;
  onEditVehicle: (vehicleId: string, opts?: { scannedVin?: string }) => void;
  onAddService: (vehicleId: string) => void;
  onAddProposal?: () => void;
  bookingProposals?: any[];
  onViewProposal?: (proposal: any) => void;
  onRemoveResource: (resourceId: string) => Promise<void>;
  onAddResource: () => void;
  assetsEnabled?: boolean;
  onRemoveAsset?: (assetId: string) => Promise<void>;
  onAddAsset?: () => void;
  onApplyDiscount: () => void;
  onDeleteDiscount: (discountId: string) => Promise<void>;
  onUpdate: () => Promise<void>;
  onError: (error: string) => void;
  onAddressUpdate?: (address: string, lat?: number, lng?: number) => Promise<void>;
  onRescheduleMobileBooking?: () => void;
  onStatusChange?: (status: string) => Promise<void>;
  onScheduleChange?: (dropOffDate: string, dropOffTime: string, startDate: string, startTime: string, endDate: string, endTime: string) => Promise<void>;
  // Damage and waiver state callbacks
  onDamageWaiverStateChange?: (state: {
    isLiabilityWaiverEnabled: boolean;
    liabilityWaiverAgreed: boolean;
    hasDamageReport: boolean;
    liabilityWaiverPdfUrl?: string;
    onSendLiabilityWaiver: () => void;
    onAddDamageReport: () => void;
    isSendingLiabilityWaiver: boolean;
  }) => void;
  onAssignWorkflow?: (serviceId: string) => void; // Callback to handle workflow assignment
  onViewInvoiceFromHeader?: () => void; // Callback to handle view invoice from header
  onSwitchToBooking?: (bookingId: string) => void; // Callback to switch to a different booking
  onFieldQuotesChanged?: () => void; // Notify parent when linked field quotes change (drives the Field Quote tab)
  onCloseBooking?: () => void;
}

export default function BookingContent({
  activeTab,
  booking,
  isSelectMaterialModalOpen,
  setIsSelectMaterialModalOpen,
  onFieldQuotesChanged,
  onEditCustomer,
  onChangeCustomer,
  onEditVehicle,
  onAddService,
  onAddProposal,
  bookingProposals,
  onViewProposal,
  onRemoveResource,
  onAddResource,
  assetsEnabled = false,
  onRemoveAsset,
  onAddAsset,
  onApplyDiscount,
  onDeleteDiscount,
  onUpdate,
  onError,
  onAddressUpdate,
  onRescheduleMobileBooking,
  onStatusChange,
  onScheduleChange,
  onSwitchToBooking,
  onDamageWaiverStateChange,
  onAssignWorkflow,
  onViewInvoiceFromHeader,
  onCloseBooking,
  isExpensesAppInstalled = false
}: BookingContentProps) {
  
  // App system hooks
  const { coreApps, installedAddons } = useAppSystem();
  const { selectedBusiness } = useBusiness();
  const navigate = useNavigate();

  // Live invoice — so Take Payment charges the invoice balance when one exists.
  const { invoice: liveInvoice, refetch: refetchLiveInvoice } = useBookingInvoice(booking?.id);

  // Issued (locked) invoice: booking edits no longer flow into it, so freeze
  // every billing surface (services, products, discounts) until it's unlocked.
  const invoiceLocked = isInvoiceLocked(liveInvoice);

  // Soft lock = amendable (open / partially_paid). Fully paid = hard lock.
  const invoiceAmendable =
    invoiceLocked && (liveInvoice?.status === 'open' || liveInvoice?.status === 'partially_paid');
  const [showAmendInvoice, setShowAmendInvoice] = useState(false);

  // Payments changed (applied / voided) — refresh the invoice rollup first so
  // paid/balance are fresh, then let the parent reload the booking itself.
  const handlePaymentsChanged = async () => {
    try {
      await refetchLiveInvoice();
    } catch { /* non-critical */ }
    await onUpdate();
  };
  
  // Check if Inventory app is installed
  const isInventoryAppInstalled = coreApps.some(app => app.app?.slug === 'inventory') || 
                                  installedAddons.some(app => app.app?.slug === 'inventory');
  
  // State for inventory settings
  const [showInventoryInBookings, setShowInventoryInBookings] = useState(true);
  const [showMaterialsInBookings, setShowMaterialsInBookings] = useState(true);
  
  // Load inventory settings
  useEffect(() => {
    const loadInventorySettings = async () => {
      if (!selectedBusiness?.id || !isInventoryAppInstalled) return;

      try {
        const { data, error } = await supabase
          .rpc('get_app_setting', {
            p_business_id: selectedBusiness.id,
            p_app_slug: 'inventory',
            p_setting_key: 'show_inventory_in_bookings',
            p_default_value: true
          });

        if (!error && data !== null) {
          setShowInventoryInBookings(data);
        }

        // Load materials setting
        const { data: materialsData, error: materialsError } = await supabase
          .rpc('get_app_setting', {
            p_business_id: selectedBusiness.id,
            p_app_slug: 'inventory',
            p_setting_key: 'show_materials_in_bookings',
            p_default_value: true
          });

        if (!materialsError && materialsData !== null) {
          setShowMaterialsInBookings(materialsData);
        }
      } catch (error) {
        console.error('Error loading inventory settings:', error);
      }
    };

    loadInventorySettings();

    // Listen for setting changes
    const handleInventorySettingChange = (event: CustomEvent) => {
      if (event.detail.key === 'show_inventory_in_bookings') {
        setShowInventoryInBookings(event.detail.value);
      }
      if (event.detail.key === 'show_materials_in_bookings') {
        setShowMaterialsInBookings(event.detail.value);
      }
    };

    window.addEventListener('inventorySettingChanged', handleInventorySettingChange as EventListener);

    return () => {
      window.removeEventListener('inventorySettingChanged', handleInventorySettingChange as EventListener);
    };
  }, [selectedBusiness?.id, isInventoryAppInstalled]);

  // Load linked project when booking has project_id
  useEffect(() => {
    const loadLinkedProject = async () => {
      if (!booking?.project_id || !supabase) {
        setLinkedProject(null);
        return;
      }

      setIsLoadingProject(true);
      try {
        const { data: projectData, error } = await supabase
          .from('projects')
          .select('*')
          .eq('id', booking.project_id)
          .single();

        if (!error && projectData) {
          setLinkedProject(projectData);
        } else {
          console.error('Error loading linked project:', error);
          setLinkedProject(null);
        }
      } catch (error) {
        console.error('Error loading linked project:', error);
        setLinkedProject(null);
      } finally {
        setIsLoadingProject(false);
      }
    };

    loadLinkedProject();
  }, [booking?.project_id]);

  // Load linked opportunity when booking has pipeline_opportunities_id
  useEffect(() => {
    const loadLinkedOpportunity = async () => {
      if (!booking?.pipeline_opportunities_id || !supabase) {
        setLinkedOpportunity(null);
        return;
      }

      setIsLoadingOpportunity(true);
      try {
        const { data: opportunityData, error } = await supabase
          .from('pipeline_opportunities')
          .select('id, title, value, status')
          .eq('id', booking.pipeline_opportunities_id)
          .single();

        if (!error && opportunityData) {
          setLinkedOpportunity({
            id: opportunityData.id,
            title: opportunityData.title,
            value: opportunityData.value || 0,
            status: opportunityData.status || 'open'
          });
        } else {
          if (error?.code !== 'PGRST116') { // PGRST116 = no rows returned
            console.error('Error loading linked opportunity:', error);
          }
          setLinkedOpportunity(null);
        }
      } catch (error) {
        console.error('Error loading linked opportunity:', error);
        setLinkedOpportunity(null);
      } finally {
        setIsLoadingOpportunity(false);
      }
    };

    loadLinkedOpportunity();
  }, [booking?.pipeline_opportunities_id]);
  
  const { canAccess, currentResource, isAdmin } = usePermissionCheck();

  // Billing edits require both the permission AND an unlocked invoice.
  const canEditBilling = canAccess.editBookingDetails && !invoiceLocked;

  // Job timer (current resource's clock in/out)
  const {
    activeEntry: activeTimerEntry,
    elapsedSeconds: timerElapsed,
    totalCompletedSeconds: timerTotalCompleted,
    loading: timerLoading,
    startTimer,
    stopTimer,
  } = useJobTimer({
    bookingId: booking?.id,
    resourceId: currentResource?.id,
    businessId: selectedBusiness?.id,
  });

  // All resources currently clocked in to this booking (for "Team / Resources working on job")
  const { activeTimers, tick: activeTimersTick, getElapsedSeconds } = useBookingActiveTimers(booking?.id);

  // Start timer: for hourly resources, require clock-in first; else start immediately
  const handleStartTimerClick = async () => {
    if (!currentResource?.id || !selectedBusiness?.id) return;
    try {
      const status = await getTimeclockStatus(selectedBusiness.id, { resourceId: currentResource.id });
      const isHourly = status.resource.compensation_type === 'hourly';
      if (isHourly && !status.entry) {
        setShowClockInPromptModal(true);
      } else {
        await startTimer('manual');
      }
    } catch (e) {
      console.error('Start timer check failed:', e);
      await startTimer('manual');
    }
  };

  const handleClockInAndStartTimer = async () => {
    if (!currentResource?.id || !selectedBusiness?.id) return;
    setClockInPromptLoading(true);
    try {
      await clockIn(selectedBusiness.id, { resourceId: currentResource.id, source: 'booking' });
      setShowClockInPromptModal(false);
      await startTimer('manual');
    } catch (e) {
      console.error('Clock in failed:', e);
      onError(e instanceof Error ? e.message : 'Failed to clock in. Please try again.');
    } finally {
      setClockInPromptLoading(false);
    }
  };

  const [isApplyDiscountModalOpen, setIsApplyDiscountModalOpen] = useState(false);
  const [isRecordPaymentModalOpen, setIsRecordPaymentModalOpen] = useState(false);
  const [isPaymentDetailsModalOpen, setIsPaymentDetailsModalOpen] = useState(false);
  const [isBetaPaymentsModalOpen, setIsBetaPaymentsModalOpen] = useState(false);
  const [isSendInvoiceModalOpen, setIsSendInvoiceModalOpen] = useState(false);
  const [isInvisiblePDFGeneration, setIsInvisiblePDFGeneration] = useState(false);
  const [showPortalToast, setShowPortalToast] = useState(false);
  const [portalToastMessage, setPortalToastMessage] = useState<React.ReactNode>(null);
  const [portalToastType, setPortalToastType] = useState<'loading' | 'success' | 'error'>('loading');
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);

  // Create portal toast instance
  const portalToast = createPortalToast(setShowPortalToast, setPortalToastMessage, setPortalToastType);

  const invoicePrincipalPaidForSendModal = useMemo(() => {
    const payments = booking.payments || [];
    const valid = payments.filter(
      (p: any) => p.status === 'completed' || p.status === 'succeeded' || p.status === 'paid',
    );
    return valid.reduce((sum: number, p: any) => sum + (p.base_amount || p.amount || 0), 0);
  }, [booking.payments]);

  const [isLoadingInvoice, setIsLoadingInvoice] = useState(false);
  const [isEditingTime, setIsEditingTime] = useState(false);
  const [timeFormData, setTimeFormData] = useState({
    drop_off_date: '',
    drop_off_time: '',
    start_date: '',
    start_time: '',
    end_date: '',
    end_time: ''
  });
  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);

  // Unscheduled bookings: schedule via the same calendar picker as create flows
  const [unscheduledPickerOpen, setUnscheduledPickerOpen] = useState(false);
  const [isSchedulingUnscheduled, setIsSchedulingUnscheduled] = useState(false);

  const [customerNotes, setCustomerNotes] = useState(booking.customer_notes || '');
  const [productTotals, setProductTotals] = useState({
    subtotal: 0,
    taxAmount: 0,
    total: 0
  });

  // Function to handle product totals update from BookingProductList
  const handleProductTotalsUpdate = (totals: { subtotal: number; taxAmount: number; total: number }) => {
    setProductTotals(totals);
  };
  const [feedbackLink, setFeedbackLink] = useState<string | null>(null);
  
  // Referral source customer state
  const [referralSourceCustomer, setReferralSourceCustomer] = useState<Customer | null>(null);
  const [isSelectReferralSourceModalOpen, setIsSelectReferralSourceModalOpen] = useState(false);
  const [loadingReferralSource, setLoadingReferralSource] = useState(false);
  const [isGeneratingFeedback, setIsGeneratingFeedback] = useState(false);
  const [feedbackCopied, setFeedbackCopied] = useState(false);
  const [bookingFeedback, setBookingFeedback] = useState<BookingFeedback | null>(null);
  const [showFeedbackLink, setShowFeedbackLink] = useState(false);
  const [showIndividualRatings, setShowIndividualRatings] = useState(false);
  const [isFeedbackEnabledForBusiness, setIsFeedbackEnabledForBusiness] = useState(true);
  
  // Feedback editing state
  const [isEditingFeedback, setIsEditingFeedback] = useState(false);
  const [editingFeedbackData, setEditingFeedbackData] = useState<{
    overall_quality_rating?: number;
    overall_experience_rating?: number;
    form_responses?: { [fieldKey: string]: string | number };
  }>({});
  const [isSavingFeedback, setIsSavingFeedback] = useState(false);
  
  
  const [feedbackFormFields, setFeedbackFormFields] = useState<FeedbackFormField[]>([]);
  const [allPaymentDetails, setAllPaymentDetails] = useState<any[]>([]);
  const [isLiabilityWaiverEnabledForBusiness, setIsLiabilityWaiverEnabledForBusiness] = useState(false);
  const [liabilityWaiver, setLiabilityWaiver] = useState<LiabilityWaiver | null>(null);
  const [isLoadingLiabilityWaiver, setIsLoadingLiabilityWaiver] = useState(true);
  const [isSendingLiabilityWaiver, setIsSendingLiabilityWaiver] = useState(false);
  const [isSendLiabilityWaiverModalOpen, setIsSendLiabilityWaiverModalOpen] = useState(false);
  const [damageReport, setDamageReport] = useState<VehicleDamageReport | null>(null);
  const [isDamageReportModalOpen, setIsDamageReportModalOpen] = useState(false);
  const [isJobTimeLogsModalOpen, setIsJobTimeLogsModalOpen] = useState(false);
  const [showClockInPromptModal, setShowClockInPromptModal] = useState(false);
  const [clockInPromptLoading, setClockInPromptLoading] = useState(false);
  const [linkedProject, setLinkedProject] = useState<any | null>(null);
  const [isLoadingProject, setIsLoadingProject] = useState(false);
  const [isProjectInfoModalOpen, setIsProjectInfoModalOpen] = useState(false);
  const [linkedOpportunity, setLinkedOpportunity] = useState<{ id: number; title: string; value: number; status: string } | null>(null);
  const [isLoadingOpportunity, setIsLoadingOpportunity] = useState(false);
  const [originalBooking, setOriginalBooking] = useState<Booking | null>(null);
  const [showServiceDetails, setShowServiceDetails] = useState(false);
  const [originalBookingServices, setOriginalBookingServices] = useState<any[]>([]);

  const dropOffDateErrorId = 'drop-off-date-error';
  const dropOffTimeErrorId = 'drop-off-time-error';
  const startDateErrorId = 'start-date-error';
  const startTimeErrorId = 'start-time-error';
  const endDateErrorId = 'end-date-error';
  const endTimeErrorId = 'end-time-error';

  // Initialize form data when booking changes
  React.useEffect(() => {
    if (booking) {
      setTimeFormData({
        drop_off_date: booking.drop_off_date || '',
        drop_off_time: booking.drop_off_time || '',
        start_date: booking.start_date || '',
        start_time: booking.start_time || '',
        end_date: booking.end_date || '',
        end_time: booking.end_time || ''
      });
      setCustomerNotes(booking.customer_notes || '');
    }
  }, [booking]);

  // Check if feedback is enabled for this business
  React.useEffect(() => {
    const checkFeedbackEnabled = async () => {
      if (booking.business_id) {
        try {
          const enabled = await isFeedbackEnabled(booking.business_id);
          setIsFeedbackEnabledForBusiness(enabled);
        } catch (error) {
          console.error('Error checking feedback enabled status:', error);
          setIsFeedbackEnabledForBusiness(false);
        }
      }
    };
    
    checkFeedbackEnabled();
  }, [booking.business_id]);

  // Load referral source customer when booking changes
  React.useEffect(() => {
    const loadReferralSourceCustomer = async () => {
      if (!booking.referral_source_customer_id) {
        setReferralSourceCustomer(null);
        return;
      }

      try {
        setLoadingReferralSource(true);
        const referralCustomer = await getCustomer(booking.referral_source_customer_id);
        setReferralSourceCustomer(referralCustomer);
      } catch (error) {
        console.error('Error loading referral source customer:', error);
        setReferralSourceCustomer(null);
      } finally {
        setLoadingReferralSource(false);
      }
    };

    loadReferralSourceCustomer();
  }, [booking.referral_source_customer_id]);

  // Use preloaded liability waiver data from booking
  React.useEffect(() => {
    if (booking.liability_waiver_enabled !== undefined) {
      // Use preloaded data - instant loading!
      setIsLiabilityWaiverEnabledForBusiness(booking.liability_waiver_enabled);
      setLiabilityWaiver(booking.liability_waiver || null);
      setIsLoadingLiabilityWaiver(false);
    } else {
      // Fallback to loading if preloaded data is not available
      const checkLiabilityWaiverEnabled = async () => {
        if (booking.business_id && supabase) {
          try {
            setIsLoadingLiabilityWaiver(true);
            const enabled = await isLiabilityWaiverEnabled(booking.business_id);
            setIsLiabilityWaiverEnabledForBusiness(enabled);
            
            // If enabled, load existing waiver data for this booking
            if (enabled) {
              const existingWaiver = await getLiabilityWaiverByBookingId(booking.id);
              setLiabilityWaiver(existingWaiver);
            } else {
              setLiabilityWaiver(null);
            }
          } catch (error) {
            console.error('Error checking liability waiver enabled status:', error);
            setIsLiabilityWaiverEnabledForBusiness(false);
            setLiabilityWaiver(null);
          } finally {
            setIsLoadingLiabilityWaiver(false);
          }
        } else {
          setIsLoadingLiabilityWaiver(false);
        }
      };
      
      checkLiabilityWaiverEnabled();
    }
  }, [booking.business_id, booking.id, booking.liability_waiver_enabled, booking.liability_waiver]);

  // Load original booking details for warranty bookings
  React.useEffect(() => {
    const loadOriginalBooking = async () => {
              if (booking.is_warranty && booking.original_booking_id) {
          try {
            console.log('🛡️ Loading original booking details for warranty booking:', booking.original_booking_id);
            const original = await getBooking(booking.original_booking_id);
            setOriginalBooking(original);
            
            // Fetch the booking services for the original booking
            if (original?.id) {
              const { data: services, error } = await supabase
                .from('booking_services')
                .select(`
                  id,
                  service_id,
                  name,
                  description,
                  price,
                  tax_amount,
                  total_amount,
                  duration_minutes,
                  services (
                    id,
                    name,
                    description
                  )
                `)
                .eq('booking_id', original.id);
              
              if (error) {
                console.error('Error loading original booking services:', error);
              } else {
                console.log('🛡️ Loaded original booking services:', services);
                setOriginalBookingServices(services || []);
              }
            }
          } catch (error) {
            console.error('Error loading original booking:', error);
            setOriginalBooking(null);
            setOriginalBookingServices([]);
          }
        } else {
          setOriginalBooking(null);
          setOriginalBookingServices([]);
        }
    };
    
    loadOriginalBooking();
  }, [booking.is_warranty, booking.original_booking_id]);

  // Use preloaded damage report data from booking
  React.useEffect(() => {
    if (booking.damage_report !== undefined) {
      // Use preloaded data - instant loading!
      setDamageReport(booking.damage_report);
    } else {
      // Fallback to loading if preloaded data is not available
      const loadDamageReport = async () => {
        if (booking.id && isLiabilityWaiverEnabledForBusiness) {
          try {
            const existingDamageReport = await getDamageReportByBookingId(booking.id);
            setDamageReport(existingDamageReport);
          } catch (error) {
            console.error('Error loading damage report:', error);
            setDamageReport(null);
          }
        }
      };
      
      loadDamageReport();
    }
  }, [booking.id, booking.damage_report, isLiabilityWaiverEnabledForBusiness]);

  // Generate feedback link for in_progress and completed bookings
  React.useEffect(() => {
    const generateFeedbackLink = async () => {
      if ((booking.status === 'completed' || booking.status === 'in_progress') && isFeedbackEnabledForBusiness) {
        if (booking.feedback_token) {
          const url = await buildFeedbackCustomerLink(booking.feedback_token, booking.business_id);
          setFeedbackLink(url);
        } else {
          // If completed but no token, try to generate one
          try {
            setIsGeneratingFeedback(true);
            const token = await generateFeedbackForBooking(booking.id);
            if (token) {
              const url = await buildFeedbackCustomerLink(token, booking.business_id);
              setFeedbackLink(url);
              // Try to refresh booking, but don't fail if it doesn't work
              try {
                await onUpdate();
              } catch (updateError) {
                console.warn('Could not refresh booking after feedback generation:', updateError);
                // Still show the link even if booking refresh fails
              }
            } else {
              console.error('Failed to generate feedback token');
            }
          } catch (error) {
            console.error('Error generating feedback link:', error);
          } finally {
            setIsGeneratingFeedback(false);
          }
        }
      } else {
        setFeedbackLink(null);
      }
    };

    generateFeedbackLink();
  }, [booking.status, booking.feedback_token, booking.id, booking.business_id, isFeedbackEnabledForBusiness]);

  // Load feedback results for in_progress and completed bookings
  React.useEffect(() => {
    const loadBookingFeedback = async () => {
      if (booking.status === 'completed' || booking.status === 'in_progress') {
        try {
          const feedback = await getBookingFeedback(booking.id);
          setBookingFeedback(feedback);
        } catch (error) {
          console.error('Error loading booking feedback:', error);
        }
      } else {
        setBookingFeedback(null);
      }
    };

    loadBookingFeedback();
  }, [booking.status, booking.id]);

  // Load feedback form fields for this business
  React.useEffect(() => {
    const loadFeedbackFormFields = async () => {
      if (booking.business_id) {
        try {
          const fields = await getAllFeedbackFormFields(booking.business_id);
          setFeedbackFormFields(fields);
        } catch (error) {
          console.error('Error loading feedback form fields:', error);
          setFeedbackFormFields([]);
        }
      }
    };

    loadFeedbackFormFields();
  }, [booking.business_id]);

  const handleCopyFeedbackLink = async () => {
    if (feedbackLink) {
      try {
        await navigator.clipboard.writeText(feedbackLink);
        setFeedbackCopied(true);
        setTimeout(() => setFeedbackCopied(false), 2000);
      } catch (error) {
        console.error('Failed to copy feedback link:', error);
        onError('Failed to copy feedback link');
      }
    }
  };

  const handleGenerateFeedbackLink = async () => {
    try {
      setIsGeneratingFeedback(true);
      const token = await generateFeedbackForBooking(booking.id);
      if (token) {
        const url = await buildFeedbackCustomerLink(token, booking.business_id);
        setFeedbackLink(url);
        // Try to refresh booking, but don't fail if it doesn't work
        try {
          await onUpdate();
        } catch (updateError) {
          console.warn('Could not refresh booking after feedback generation:', updateError);
          // Still show the link even if booking refresh fails
        }
      } else {
        onError('Failed to generate feedback link');
      }
    } catch (error) {
      console.error('Error generating feedback link:', error);
      onError('Failed to generate feedback link');
    } finally {
      setIsGeneratingFeedback(false);
    }
  };

  // Feedback editing functions
  const handleStartEditingFeedback = () => {
    if (!bookingFeedback) return;
    
    // Initialize editing data with current feedback values
    const responses = (bookingFeedback as ExtendedBookingFeedback).form_responses || {};
    setEditingFeedbackData({
      overall_quality_rating: bookingFeedback.overall_quality_rating,
      overall_experience_rating: bookingFeedback.overall_experience_rating,
      form_responses: Object.keys(responses).length > 0 ? { ...responses } : undefined
    });
    setIsEditingFeedback(true);
  };

  const handleCancelEditingFeedback = () => {
    setIsEditingFeedback(false);
    setEditingFeedbackData({});
  };

  const handleSaveFeedback = async () => {
    if (!bookingFeedback) return;
    
    try {
      setIsSavingFeedback(true);
      
      const updatedFeedback = await updateFeedbackRatings(bookingFeedback.id, editingFeedbackData);
      
      if (updatedFeedback) {
        setBookingFeedback(updatedFeedback);
        setIsEditingFeedback(false);
        setEditingFeedbackData({});
        onError('Feedback ratings updated successfully');
      }
    } catch (error) {
      console.error('Error saving feedback ratings:', error);
      onError(error instanceof Error ? error.message : 'Failed to save feedback ratings');
    } finally {
      setIsSavingFeedback(false);
    }
  };

  const handleRatingChange = (fieldKey: string, newRating: number) => {
    setEditingFeedbackData(prev => {
      const responses = (bookingFeedback as ExtendedBookingFeedback).form_responses || {};
      
      if (Object.keys(responses).length > 0) {
        // Dynamic form responses
        return {
          ...prev,
          form_responses: {
            ...prev.form_responses,
            [fieldKey]: newRating
          }
        };
      } else {
        // Legacy fields
        if (fieldKey === 'overall_quality_rating') {
          return { ...prev, overall_quality_rating: newRating };
        } else if (fieldKey === 'overall_experience_rating') {
          return { ...prev, overall_experience_rating: newRating };
        }
      }
      
      return prev;
    });
  };

  // Helper component to render star ratings
  const StarRating = ({ rating, size = 'small' }: { rating: number; size?: 'small' | 'large' }) => {
    const starSize = size === 'large' ? 'h-5 w-5' : 'h-4 w-4';
    
    return (
      <div className="flex space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`${starSize} ${
              star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'
            }`}
          />
        ))}
      </div>
    );
  };

  // Handle send method selection from SendInvoiceModal
  const handleSendMethodSelect = async (
    method: 'email' | 'sms',
    allowStripePay: boolean,
    _depositAmount?: number,
    saveCardOnFile?: boolean,
  ) => {
    console.log('BookingContent - Method selected:', method, 'Allow Stripe Pay:', allowStripePay);
    
    // Close the SendInvoiceModal first
    setIsSendInvoiceModalOpen(false);
    
    if (method === 'email') {
      // Send invoice directly without opening BetaPaymentsModal
      await handleDirectSendInvoice(allowStripePay, saveCardOnFile);
    } else {
      // SMS functionality coming soon
      portalToast.error('SMS functionality coming soon!');
    }
  };

  // Send invoice directly without opening any modals
  const handleDirectSendInvoice = async (allowStripePay: boolean = true, saveCardOnFile?: boolean) => {
    if (!booking.customer?.email || !selectedBusiness) {
      portalToast.error('Customer email not found');
      return;
    }

    // Show immediate sending feedback with loading
    const loadingToast = portalToast.loading(
      <div>
        <div>Sending invoice...</div>
        <div style={{ fontSize: '12px', opacity: 0.7, marginTop: '2px' }}>don't close booking</div>
      </div>, 
      {
        position: 'top-center'
      }
    );

    try {
      // Generate PDF via server-side edge function (no DOM/modal needed)
      const { data: pdfResult, error: pdfError } = await supabase.functions.invoke('generate-invoice-pdf', {
        body: { bookingId: booking.id }
      });

      if (pdfError || !pdfResult) {
        portalToast.dismiss(loadingToast);
        portalToast.error('Failed to generate PDF. Please try again.');
        return;
      }

      const base64Content = pdfResult.base64;
      const pdfFilename = pdfResult.filename || `invoice-${booking.booking_number}.pdf`;

      // Generate payment link if balance is owed and Stripe Connect is enabled
      let paymentLinkHtml = '';
      const currentTotal = booking?.total_amount || 0;
      const payments = booking?.payments || [];
      const validPayments = payments.filter((payment: any) => 
        payment.status === 'completed' || payment.status === 'succeeded' || payment.status === 'paid'
      );
      const totalPaid = validPayments.reduce((sum: number, payment: any) => sum + (payment.base_amount || payment.amount || 0), 0);
      const amountOwing = Math.max(0, currentTotal - totalPaid);

      if (amountOwing > 0 && allowStripePay && (selectedBusiness as any)?.stripe_connect_account_id && (selectedBusiness as any)?.online_payments_enabled !== false) {
        try {
          console.log('🔗 Generating payment link for invoice...');
          
          // Smart payment record management - prevent duplicates and reuse links
          let unpaidPayment = payments.find((p: any) => 
            p.status === 'pending' && 
            (p.amount === 0 || p.reference === 'unpaid-invoice')
          );
          
          let paymentUrl = null;
          
          if (!unpaidPayment) {
            console.log('🔍 No existing pending payment found, creating new one...');
            const customerId = booking.customer?.id;
            // Create unpaid invoice record
            const { data: newPayment, error: paymentError } = await supabase
              .from('payments')
              .insert({
                business_id: selectedBusiness?.id || '',
                booking_id: booking.id,
                amount: 0, // Placeholder for unpaid invoice
                method: 'credit_card',
                status: 'pending',
                reference: 'unpaid-invoice',
                notes: `Unpaid invoice for booking #${booking.booking_number}`,
                payment_token: crypto.randomUUID(),
                save_card_on_file: !!(saveCardOnFile && customerId && allowStripePay),
                save_card_customer_id:
                  saveCardOnFile && customerId && allowStripePay ? customerId : null,
              })
              .select()
              .single();

            if (paymentError) {
              console.error('Failed to create unpaid payment record:', paymentError);
            } else {
              unpaidPayment = newPayment;
              console.log('✅ Created new unpaid payment record:', unpaidPayment?.id);
            }
          } else {
            console.log('✅ Found existing pending payment record:', unpaidPayment?.id);
          }

          if (unpaidPayment && allowStripePay) {
            const customerId = booking.customer?.id;
            const saveFlag = !!(saveCardOnFile && customerId);
            const { error: saveFlagsErr } = await supabase
              .from('payments')
              .update({
                save_card_on_file: saveFlag,
                save_card_customer_id: saveFlag ? customerId : null,
              })
              .eq('id', unpaidPayment.id);
            if (saveFlagsErr) {
              console.error('Failed to update save-card flags on pending payment:', saveFlagsErr);
            }
          }

          if (unpaidPayment) {
            // Check if this payment already has a payment link
            const { data: existingLink, error: linkCheckError } = await supabase
              .from('payment_links')
              .select('payment_url')
              .eq('payment_id', unpaidPayment.id)
              .eq('status', 'active')
              .single();

            if (!linkCheckError && existingLink?.payment_url) {
              // Reuse existing payment link
              paymentUrl = existingLink.payment_url;
              console.log('♻️ Reusing existing payment link:', paymentUrl);
            } else {
              // Generate new payment link
              console.log('🔗 Generating new payment link...');
              const { data: linkData, error: linkError } = await supabase.functions.invoke('generate-invoice-payment-link', {
                body: { paymentId: unpaidPayment.id }
              });

              if (!linkError && linkData?.paymentUrl) {
                paymentUrl = linkData.paymentUrl;
                console.log('✅ New payment link generated:', paymentUrl);
              } else {
                console.error('❌ Failed to generate payment link:', linkError);
              }
            }

            if (paymentUrl) {
              paymentLinkHtml = `
                <div style="margin: 30px 0; padding: 25px; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); border-radius: 12px; text-align: center;">
                  <h3 style="color: white; margin: 0 0 15px 0; font-size: 18px;">Pay Your Invoice Online</h3>
                  <p style="color: #e2e8f0; margin: 0 0 20px 0; font-size: 14px;">Secure payment with credit or debit card</p>
                  <a href="${paymentUrl}" 
                     style="display: inline-block; background: #ffffff; color: #667eea; padding: 12px 30px; text-decoration: none; border-radius: 8px; font-weight: bold; font-size: 16px; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);">
                    Pay $${amountOwing.toFixed(2)} Now
                  </a>
                  <p style="color: #cbd5e0; margin: 15px 0 0 0; font-size: 12px;">Powered by Stripe - Secure &amp; Encrypted</p>
                </div>
              `;
            }
          }
        } catch (paymentLinkError) {
          console.error('Error generating payment link:', paymentLinkError);
          // Continue without payment link
        }
      }

      // Create email body
      const emailBody = `
        <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 600px; margin: 0 auto; background: #ffffff;">
          <div style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 30px; text-align: center; border-radius: 12px 12px 0 0;">
            <h1 style="color: white; margin: 0; font-size: 28px; font-weight: bold;">Invoice from ${selectedBusiness?.name || 'Business'}</h1>
            <p style="color: #e2e8f0; margin: 10px 0 0 0; font-size: 16px;">Booking #${booking.booking_number}</p>
          </div>
          
          <div style="padding: 30px; background: #f8fafc;">
            <p style="color: #374151; font-size: 16px; line-height: 1.6; margin: 0 0 20px 0;">
              Hi ${booking.customer?.first_name || 'there'},
            </p>
            
            <p style="color: #374151; font-size: 16px; line-height: 1.6; margin: 0 0 20px 0;">
              Thank you for choosing ${selectedBusiness?.name || 'our business'}! Please find your invoice details below.
            </p>
            
            ${amountOwing > 0 ? `
              <p style="color: #374151; font-size: 16px; line-height: 1.6; margin: 0 0 20px 0;">
                <strong>Amount Due: $${amountOwing.toFixed(2)}</strong>
              </p>
            ` : `
              <p style="color: #059669; font-size: 16px; line-height: 1.6; margin: 0 0 20px 0; font-weight: bold;">
                ✅ This invoice has been paid in full. Thank you!
              </p>
            `}
            
            ${paymentLinkHtml}
            
            <div style="margin: 20px 0; padding: 20px; background: #ffffff; border: 1px solid #e5e7eb; border-radius: 8px;">
              <h3 style="margin: 0 0 15px 0; color: #374151;">Invoice Summary</h3>
              <div style="display: flex; justify-content: space-between; margin-bottom: 10px;">
                <span style="color: #6b7280;">Subtotal:</span>
                <span style="color: #374151; font-weight: bold;">$${(booking.subtotal || 0).toFixed(2)}</span>
              </div>
              <div style="display: flex; justify-content: space-between; margin-bottom: 10px;">
                <span style="color: #6b7280;">Tax:</span>
                <span style="color: #374151; font-weight: bold;">$${(booking.tax_amount || 0).toFixed(2)}</span>
              </div>
              <div style="display: flex; justify-content: space-between; padding-top: 10px; border-top: 1px solid #e5e7eb;">
                <span style="color: #374151; font-weight: bold;">Total:</span>
                <span style="color: #374151; font-weight: bold;">$${currentTotal.toFixed(2)}</span>
              </div>
            </div>
            
            <p style="color: #6b7280; font-size: 14px; line-height: 1.6; margin: 20px 0 0 0;">
              If you have any questions about this invoice, please don't hesitate to contact us.
            </p>
          </div>
          
          <div style="background: #f8fafc; padding: 20px; text-align: center; border-radius: 0 0 12px 12px; border-top: 1px solid #e5e7eb;">
            <p style="color: #6b7280; font-size: 14px; margin: 0;">This is an automated invoice from ${selectedBusiness?.name || 'Business'}</p>
          </div>
        </div>
      `;
      
      // Send email with PDF attachment
      const result = await sendEmailWithFallback({
        to: booking.customer?.email || '',
        subject: `Invoice #${booking.booking_number} from ${selectedBusiness?.name || 'Business'}${amountOwing > 0 ? ' - Pay Online' : ' - Thank You'}`,
        body: emailBody,
        businessId: selectedBusiness?.id || '',
        attachments: [{
          filename: pdfFilename,
          content: base64Content,
          type: 'application/pdf'
        }]
      });
      
      if (result.success) {
        console.log('✅ Invoice sent successfully!');
        portalToast.dismiss(loadingToast);
        portalToast.success('Invoice sent successfully! ✅', {
          duration: 4000,
          position: 'top-center'
        });
        
        // Refresh the booking data to show updated invoice sent status
        if (onUpdate) {
          try {
            const result = onUpdate();
            if (result instanceof Promise) {
              await result;
            }
            console.log('✅ Booking data refreshed after invoice send');
          } catch (error) {
            console.error('❌ Failed to refresh booking data:', error);
          }
        }
      } else {
        console.error('❌ Failed to send invoice:', result.error);
        portalToast.dismiss(loadingToast);
        portalToast.error('Failed to send invoice. Please try again.', {
          duration: 4000,
          position: 'top-center'
        });
      }
      
    } catch (error) {
      console.error('❌ Error sending invoice:', error);
      portalToast.dismiss(loadingToast);
      portalToast.error('Failed to send invoice. Please try again.', {
        duration: 4000,
        position: 'top-center'
      });
    }
  };

  // generateInvisiblePDF is no longer needed — PDF generation is handled server-side via the edge function

  const handleViewInvoice = async () => {
    try {
      setIsLoadingInvoice(true);
      
      // Always fetch fresh booking data to ensure complete service details
      const freshBooking = await getBooking(booking.id);
      if (!freshBooking) {
        throw new Error('Could not load booking details');
      }

      // Create all payment details objects for the modal
      const allPayments: any[] = [];

      // Add existing payments with fresh booking data
      const existingPayments = (booking.payments || []).map(payment => {
        return {
          id: payment.id,
          business_id: freshBooking.business_id,
          booking_id: freshBooking.id,
          amount: payment.amount,
          status: payment.status as 'pending' | 'completed' | 'voided',
          created_at: payment.created_at,
          notes: undefined,
          booking_number: typeof freshBooking.booking_number === 'number' ? freshBooking.booking_number : parseInt(String(freshBooking.booking_number)) || 0,
          booking_title: freshBooking.title,
          booking_subtotal: freshBooking.subtotal,
          customer_first_name: freshBooking.customer?.first_name || '',
          customer_last_name: freshBooking.customer?.last_name || '',
          customer_email: freshBooking.customer?.email || '',
          booking_services: freshBooking.services?.map(s => ({
            id: s.id,
            name: s.name,
            description: s.description,
            price: s.price,
            tax_amount: s.tax_amount,
            total_amount: s.total_amount,
            duration_minutes: s.duration_minutes,
            vehicle_make: s.vehicle_make || '',
            vehicle_model: s.vehicle_model || '',
            add_ons: s.addons?.map((a: any) => ({
              id: a.id,
              name: a.addon?.name || 'Add-on',
              description: a.addon?.description || '',
              price: a.price
            })) || []
          })) || [],
          booking_discounts: freshBooking.discounts?.map(d => ({
            id: d.id,
            title: d.title,
            type: d.type as 'percentage' | 'fixed',
            amount: d.amount
          })) || []
        };
      });

      allPayments.push(...existingPayments);

      // If no payments exist, create a pending invoice
      if (!booking.payments || booking.payments.length === 0) {
        const pendingInvoice = {
          id: 'pending',
          business_id: freshBooking.business_id,
          booking_id: freshBooking.id,
          amount: freshBooking.total_amount || 0,
          status: 'pending' as const,
          created_at: new Date().toISOString(),
          notes: undefined,
          booking_number: typeof freshBooking.booking_number === 'number' ? freshBooking.booking_number : parseInt(String(freshBooking.booking_number)) || 0,
          booking_title: freshBooking.title,
          booking_subtotal: freshBooking.subtotal,
          customer_first_name: freshBooking.customer?.first_name || '',
          customer_last_name: freshBooking.customer?.last_name || '',
          customer_email: freshBooking.customer?.email || '',
          booking_services: freshBooking.services?.map(s => ({
            id: s.id,
            name: s.name,
            description: s.description,
            price: s.price,
            tax_amount: s.tax_amount,
            total_amount: s.total_amount,
            duration_minutes: s.duration_minutes,
            vehicle_make: s.vehicle_make || '',
            vehicle_model: s.vehicle_model || '',
            add_ons: s.addons?.map((a: any) => ({
              id: a.id,
              name: a.addon?.name || 'Add-on',
              description: a.addon?.description || '',
              price: a.price
            })) || []
          })) || [],
          booking_discounts: freshBooking.discounts?.map(d => ({
            id: d.id,
            title: d.title,
            type: d.type as 'percentage' | 'fixed',
            amount: d.amount
          })) || []
        };
        allPayments.push(pendingInvoice);
      } else {
        // Check if there's a remaining balance for partial payments
        const totalPaid = (booking.payments || [])
          .filter(p => p.status === 'completed')
          .reduce((sum, p) => sum + p.amount, 0);
        
        const remainingBalance = (freshBooking.total_amount || 0) - totalPaid;
        
        if (remainingBalance > 0) {
          // Create an unpaid invoice for the remaining balance
          const remainingInvoice = {
            id: 'pending-balance',
            business_id: freshBooking.business_id,
            booking_id: freshBooking.id,
            amount: remainingBalance,
            status: 'pending' as const,
            created_at: new Date().toISOString(),
            notes: undefined,
            booking_number: typeof freshBooking.booking_number === 'number' ? freshBooking.booking_number : parseInt(String(freshBooking.booking_number)) || 0,
            booking_title: freshBooking.title,
            booking_subtotal: freshBooking.subtotal,
            customer_first_name: freshBooking.customer?.first_name || '',
            customer_last_name: freshBooking.customer?.last_name || '',
            customer_email: freshBooking.customer?.email || '',
            booking_services: freshBooking.services?.map(s => ({
              id: s.id,
              name: s.name,
              description: s.description,
              price: s.price,
              tax_amount: s.tax_amount,
              total_amount: s.total_amount,
              duration_minutes: s.duration_minutes,
              vehicle_make: s.vehicle_make || '',
              vehicle_model: s.vehicle_model || '',
              add_ons: s.addons?.map((a: any) => ({
                id: a.id,
                name: a.addon?.name || 'Add-on',
                description: a.addon?.description || '',
                price: a.price
              })) || []
            })) || [],
            booking_discounts: freshBooking.discounts?.map(d => ({
              id: d.id,
              title: d.title,
              type: d.type as 'percentage' | 'fixed',
              amount: d.amount
            })) || []
          };
          allPayments.push(remainingInvoice);
        }
      }
      
      // Set selectedPayment to trigger PaymentDetailsModal to open
      setSelectedPayment(allPayments[0] || null);
      setIsPaymentDetailsModalOpen(true);

      // Store allPayments in a state for the modal
      setAllPaymentDetails(allPayments);
    } catch (err) {
      console.error('Error loading booking for invoice:', err);
      onError('Failed to load invoice details');
    } finally {
      setIsLoadingInvoice(false);
    }
  };

  const handleSaveTime = async () => {
    try {
      // Use onScheduleChange if available (for automation cancel/retrigger logic)
      if (onScheduleChange) {
        // For mobile bookings, pass empty strings for drop_off fields
        const dropOffDate = booking.mobile_booking ? '' : (timeFormData.drop_off_date || '');
        const dropOffTime = booking.mobile_booking ? '' : (timeFormData.drop_off_time || '');
        
        await onScheduleChange(
          dropOffDate,
          dropOffTime,
          timeFormData.start_date,
          timeFormData.start_time,
          timeFormData.end_date,
          timeFormData.end_time
        );
      } else {
        // Fallback to direct update - exclude drop_off for mobile bookings
        const updateData: any = {
          start_date: timeFormData.start_date,
          start_time: timeFormData.start_time,
          end_date: timeFormData.end_date,
          end_time: timeFormData.end_time
        };
        
        // Only include drop_off fields for location bookings (not mobile)
        if (!booking.mobile_booking) {
          updateData.drop_off_date = timeFormData.drop_off_date;
          updateData.drop_off_time = timeFormData.drop_off_time;
        }
        
        await updateBooking(booking.id, updateData);
        await onUpdate();
      }
      setIsEditingTime(false);
    } catch (err) {
      console.error('Error updating times:', err);
      onError(err instanceof Error ? err.message : 'Failed to update times');
    }
  };

  // Total service duration (minutes) used to auto-fill the end time in the picker.
  const unscheduledServiceDuration = useMemo(() => {
    const total = (booking.services || []).reduce(
      (sum, s: any) => sum + (s?.duration_minutes || 0),
      0
    );
    return Math.max(60, total);
  }, [booking.services]);

  // Schedule a previously unscheduled ("limbo") booking: set its dates, clear the
  // unscheduled flag, and move it to the scheduled status so it lands on the calendar.
  const handleScheduleUnscheduled = async (
    startDate: string,
    startTime: string,
    endDate: string,
    endTime: string
  ) => {
    if (isSchedulingUnscheduled) return;
    setIsSchedulingUnscheduled(true);
    try {
      await updateBooking(booking.id, {
        start_date: startDate,
        start_time: startTime,
        end_date: endDate,
        end_time: endTime,
        is_unscheduled: false,
        status: 'scheduled'
      });
      // The booking now has a real date — THIS is its "created" moment for
      // automations (suppressed while it sat unscheduled).
      if (selectedBusiness?.id) {
        void triggerBookingCreatedAfterScheduling(selectedBusiness.id, booking.id);
      }
      setUnscheduledPickerOpen(false);
      await onUpdate();
    } catch (err) {
      console.error('Error scheduling unscheduled booking:', err);
      onError(err instanceof Error ? err.message : 'Failed to schedule booking');
    } finally {
      setIsSchedulingUnscheduled(false);
    }
  };

  const handleSaveCustomerNotes = async (notes: string) => {
    try {
      await updateBooking(booking.id, { customer_notes: notes });
      setCustomerNotes(notes);
      await onUpdate();
    } catch (err) {
      console.error('Error updating customer notes:', err);
      onError(err instanceof Error ? err.message : 'Failed to update customer notes');
    }
  };

  const handleColorChange = async (color: string) => {
    try {
      await updateBooking(booking.id, { color });
      await onUpdate();
    } catch (err) {
      console.error('Error changing color:', err);
      onError(err instanceof Error ? err.message : 'Failed to change color');
    }
  };

  const handleSourceUpdate = async (sourceName: string | null) => {
    if (!booking.id) return;
    
    try {
      await updateBooking(booking.id, {
        source: sourceName || undefined
      });
      
      // Update local booking state
      onUpdate?.();
    } catch (error) {
      console.error('Error updating booking source:', error);
      onError?.(error instanceof Error ? error.message : 'Failed to update booking source');
    }
  };

  const handleReferralSourceUpdate = async (customerId: string | null) => {
    if (!booking.id) return;
    
    try {
      await updateBooking(booking.id, {
        referral_source_customer_id: customerId || null
      });
      
      // Apply referral discount if conditions are met
      if (
        customerId &&
        booking.source?.toLowerCase() === 'referral' &&
        booking.customer &&
        !booking.customer.repeat && // Customer is new
        selectedBusiness?.id
      ) {
        try {
          console.log('🎁 Applying referral discount to booking:', booking.id);
          const discountResult = await applyReferralDiscount(booking.id, selectedBusiness.id);
          if (discountResult.success) {
            console.log('✅ Referral discount applied successfully');
          } else {
            console.warn('⚠️ Failed to apply referral discount:', discountResult.error);
            // Don't throw - booking is already updated, discount is supplementary
          }
        } catch (discountError) {
          console.error('❌ Error applying referral discount:', discountError);
          // Don't throw - booking is already updated, discount is supplementary
        }
      }
      
      // Update local booking state
      onUpdate?.();
    } catch (error) {
      console.error('Error updating referral source:', error);
      onError?.(error instanceof Error ? error.message : 'Failed to update referral source');
    }
  };

  const handleRecordPayment = () => {
    setIsRecordPaymentModalOpen(true);
  };


  const handleSendLiabilityWaiver = () => {
    setIsSendLiabilityWaiverModalOpen(true);
  };

  const handleSendLiabilityWaiverSMS = async () => {
    if (!booking.customer?.phone) {
      onError('Customer phone number is required to send liability waiver');
      setIsSendLiabilityWaiverModalOpen(false);
      return;
    }

    try {
      setIsSendingLiabilityWaiver(true);
      
      const customerName = `${booking.customer.first_name || ''} ${booking.customer.last_name || ''}`.trim();
      const businessName = selectedBusiness?.name || 'Your Service Provider';
      
      const success = await sendLiabilityWaiverSMS(
        booking.id,
        booking.customer.phone,
        customerName,
        businessName
      );
      
      if (success) {
        // Reload waiver data to get the updated token
        const updatedWaiver = await getLiabilityWaiverByBookingId(booking.id);
        setLiabilityWaiver(updatedWaiver);
        
        // Show success message
        console.log('Liability waiver SMS sent successfully');
        setIsSendLiabilityWaiverModalOpen(false);
      }
    } catch (error) {
      console.error('Error sending liability waiver SMS:', error);
      onError('Failed to send liability waiver SMS');
    } finally {
      setIsSendingLiabilityWaiver(false);
    }
  };

  const handleSendLiabilityWaiverEmail = async () => {
    if (!booking.customer?.email) {
      onError('Customer email address is required to send liability waiver');
      setIsSendLiabilityWaiverModalOpen(false);
      return;
    }

    try {
      setIsSendingLiabilityWaiver(true);
      
      const customerName = `${booking.customer.first_name || ''} ${booking.customer.last_name || ''}`.trim();
      const businessName = selectedBusiness?.name || 'Your Service Provider';
      
      const success = await sendLiabilityWaiverEmail(
        booking.id,
        booking.customer.email,
        customerName,
        businessName
      );
      
      if (success) {
        // Reload waiver data to get the updated token
        const updatedWaiver = await getLiabilityWaiverByBookingId(booking.id);
        setLiabilityWaiver(updatedWaiver);
        
        // Show success message
        console.log('Liability waiver email sent successfully');
        setIsSendLiabilityWaiverModalOpen(false);
      }
    } catch (error) {
      console.error('Error sending liability waiver email:', error);
      onError('Failed to send liability waiver email');
    } finally {
      setIsSendingLiabilityWaiver(false);
    }
  };

  const handleSignLiabilityWaiverOnDevice = async () => {
    try {
      setIsSendingLiabilityWaiver(true);
      
      // Detect mobile/PWA environment
      const isMobile = window.innerWidth < 768;
      const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
                    (window.navigator as any).standalone || 
                    document.referrer.includes('android-app://');
      
      // Generate or get existing waiver token
      const token = await generateLiabilityWaiver(booking.id);
      if (!token) {
        throw new Error('Failed to generate liability waiver token');
      }

      // Generate waiver URL
      const waiverURL = generateLiabilityWaiverURL(token);
      
      // Use different approaches based on environment
      if (isMobile || isPWA) {
        // For mobile/PWA: Redirect in same window to avoid popup blockers
        console.log('🚗 [WAIVER] Mobile/PWA detected, redirecting in same window');
        window.location.href = waiverURL;
      } else {
        // For desktop: Open in new tab
        console.log('🚗 [WAIVER] Desktop detected, opening in new tab');
        window.open(waiverURL, '_blank');
      }
      
      setIsSendLiabilityWaiverModalOpen(false);
    } catch (err) {
      console.error('Error opening liability waiver on device:', err);
      onError(err instanceof Error ? err.message : 'Failed to open liability waiver');
    } finally {
      setIsSendingLiabilityWaiver(false);
    }
  };

  const handleAddDamageReport = () => {
    setIsDamageReportModalOpen(true);
  };

  const handleDamageReportSave = (savedReport: VehicleDamageReport) => {
    setDamageReport(savedReport);
    setIsDamageReportModalOpen(false);
  };

  // Notify parent of damage/waiver state changes
  useEffect(() => {
    if (onDamageWaiverStateChange) {
      onDamageWaiverStateChange({
        isLiabilityWaiverEnabled: isLiabilityWaiverEnabledForBusiness,
        liabilityWaiverAgreed: !!liabilityWaiver?.agreed_at,
        hasDamageReport: !!damageReport,
        liabilityWaiverPdfUrl: liabilityWaiver?.pdf_url,
        onSendLiabilityWaiver: handleSendLiabilityWaiver,
        onAddDamageReport: handleAddDamageReport,
        isSendingLiabilityWaiver
      });
    }
  }, [
    isLiabilityWaiverEnabledForBusiness,
    liabilityWaiver,
    damageReport,
    isSendingLiabilityWaiver,
    onDamageWaiverStateChange
  ]);









  if (activeTab === 'field-quote') {
    const isMobile = window.innerWidth < 640;
    return (
      <div className={isMobile ? 'p-2 space-y-2' : 'p-4 space-y-2'}>
        <BookingFieldQuotesSection
          bookingId={booking.id}
          businessId={booking.business_id}
          customerId={booking.customer?.id ?? null}
          opportunityId={(booking as any).pipeline_opportunities_id ?? null}
          projectId={booking.project_id ?? null}
          canEdit={canAccess.editBookingDetails}
          isMobile={isMobile}
          hideChrome
          onChange={onFieldQuotesChanged}
        />
      </div>
    );
  }

  if (activeTab === 'overview') {
    const isMobile = window.innerWidth < 640;
    
    return (
      <div 
        className="space-y-2"
        style={{ 
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif'
        }}
      >
        {/* Warranty Information Section - Only for Warranty Bookings */}
        {booking.is_warranty && (
          <div className="bg-white rounded-[12px] border border-gray-200/60 overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
            <div className="p-6">
              <div className="flex items-center mb-2">
                <h3 className="text-xs sm:text-sm font-bold text-gray-900">
                  Warranty Information
                </h3>
              </div>
              <div className="space-y-2">

                
                {booking.original_booking_id && (
                  <div className="space-y-2">
                    <div className="text-xs font-medium text-gray-700 mb-1">
                      Warranty for:
                    </div>
                    <div className="bg-gray-50/80 rounded-lg p-2 sm:p-3 border border-gray-200/30">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between text-xs sm:text-sm space-y-2 sm:space-y-0">
                        <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-4 lg:space-x-6">
                          <div className="flex items-center space-x-1.5 sm:space-x-2">
                            <span className="font-medium text-gray-700">Booking #:</span>
                            <span className="text-gray-900 font-mono">
                              {originalBooking?.booking_number || booking.original_booking_id.slice(-8)}
                            </span>
                          </div>
                          <div className="flex items-center space-x-1.5 sm:space-x-2">
                            <span className="font-medium text-gray-700">Completed:</span>
                            <span className="text-gray-900">
                              {originalBooking?.completed_at ? 
                                new Date(originalBooking.completed_at).toLocaleDateString() + ' ' + 
                                new Date(originalBooking.completed_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) 
                                : 'Loading...'}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-1.5 sm:space-x-2">
                          <span className="font-medium text-gray-700">Services:</span>
                          <button 
                            onClick={() => setShowServiceDetails(!showServiceDetails)}
                            className="flex items-center text-gray-900 hover:text-purple-600 transition-colors text-xs sm:text-sm"
                          >
                            <span className="font-medium">{originalBookingServices.length} service(s)</span>
                            <ChevronDown className={`h-3 w-3 sm:h-4 sm:w-4 ml-1 sm:ml-1.5 transition-transform ${showServiceDetails ? 'rotate-180' : ''}`} />
                          </button>
                        </div>
                      </div>
                        {showServiceDetails && originalBookingServices.length > 0 && (
                          <div className="mt-2 sm:mt-3 space-y-1.5 sm:space-y-2">
                            {originalBookingServices.map((service, index) => (
                              <div key={index} className="bg-white/80 rounded-lg p-2 sm:p-2.5 border border-gray-200/50">
                                <div className="font-semibold text-gray-800 text-xs sm:text-sm">{service.name}</div>
                                {service.description && (
                                  <div className="text-gray-600 text-xs mt-0.5 sm:mt-1 leading-relaxed">{service.description}</div>
                                )}
                                <div className="text-gray-700 font-medium mt-1 sm:mt-1.5 text-xs sm:text-sm">${service.total_amount?.toFixed(2) || service.price?.toFixed(2) || '0.00'}</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                )}
                

              </div>
            </div>
          </div>
        )}

        {/* Linked Project Section - Show if booking is linked to a project */}
        {booking.project_id && linkedProject && (
          <div className="bg-white rounded-[12px] border border-gray-200/60 overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.05)] mb-3">
            <div className={`${isMobile ? 'px-3 py-2.5' : 'px-4 py-3'} border-b border-gray-200/60 bg-gray-50/50`}>
              <h3 className="text-sm sm:text-base font-semibold text-gray-900">
                Linked Project
              </h3>
            </div>
            <div className={`${isMobile ? 'p-3' : 'p-4'}`}>
              <button
                onClick={() => setIsProjectInfoModalOpen(true)}
                className="w-full text-left hover:bg-gray-50 active:bg-gray-100 rounded-lg transition-colors p-3 -m-3"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {linkedProject.title}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Status: {linkedProject.status}
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0 ml-2" />
                </div>
              </button>
            </div>
          </div>
        )}

        {/* Recurring booking — single-line indicator above the schedule */}
        {(booking as any).booking_series_id && (
          <BookingSeriesLine
            seriesId={(booking as any).booking_series_id}
            currentBookingId={booking.id}
            isMobile={isMobile}
            onRefresh={onUpdate}
            onCurrentVisitDeleted={onCloseBooking}
          />
        )}

        {/* Date and Time Information - iOS Style */}
        <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
          <div className={`${isMobile ? 'p-2' : 'p-3'}`}>
            {booking.is_unscheduled ? (
              canAccess.editBookingDetails ? (
                <>
                  <button
                    type="button"
                    onClick={() => setUnscheduledPickerOpen(true)}
                    disabled={isSchedulingUnscheduled}
                    className={`w-full flex items-center justify-center gap-2 ${isMobile ? 'px-4 py-3 text-[15px]' : 'px-4 py-2.5 text-sm'} font-semibold text-white bg-blue-600 rounded-[12px] hover:bg-blue-700 active:bg-blue-800 active:scale-[0.98] transition-all duration-150 touch-manipulation shadow-sm disabled:opacity-50 disabled:cursor-not-allowed`}
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    {isSchedulingUnscheduled ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Scheduling…
                      </>
                    ) : (
                      <>
                        <Calendar className="w-4 h-4" />
                        Schedule
                      </>
                    )}
                  </button>

                  {unscheduledPickerOpen && selectedBusiness?.id && (
                    <SchedulePickerPanel
                      isOpen={unscheduledPickerOpen}
                      onClose={() => setUnscheduledPickerOpen(false)}
                      businessId={selectedBusiness.id}
                      showDropOff={false}
                      defaultView="month"
                      startDate=""
                      startTime=""
                      endDate=""
                      endTime=""
                      serviceDuration={unscheduledServiceDuration}
                      onConfirm={(sDate, sTime, eDate, eTime) => {
                        void handleScheduleUnscheduled(sDate, sTime, eDate, eTime);
                      }}
                      disabled={isSchedulingUnscheduled}
                    />
                  )}
                </>
              ) : (
                <div className="text-[13px] text-gray-500 font-medium text-center py-2">
                  Unscheduled — no date set yet
                </div>
              )
            ) : isEditingTime && canAccess.editBookingDetails ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Drop-off Date</label>
                    <input
                      type="date"
                      value={timeFormData.drop_off_date}
                      onChange={(e) => setTimeFormData(prev => ({ ...prev, drop_off_date: e.target.value }))}
                      className="w-full h-9 px-3 text-[13px] border border-gray-300 rounded-[10px] focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all touch-manipulation"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Drop-off Time</label>
                    <input
                      type="time"
                      value={timeFormData.drop_off_time}
                      onChange={(e) => setTimeFormData(prev => ({ ...prev, drop_off_time: e.target.value }))}
                      className="w-full h-9 px-3 text-[13px] border border-gray-300 rounded-[10px] focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all touch-manipulation"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Start Date</label>
                    <input
                      type="date"
                      value={timeFormData.start_date}
                      onChange={(e) => setTimeFormData(prev => ({ ...prev, start_date: e.target.value }))}
                      className="w-full h-9 px-3 text-[13px] border border-gray-300 rounded-[10px] focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all touch-manipulation"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Start Time</label>
                    <input
                      type="time"
                      value={timeFormData.start_time}
                      onChange={(e) => setTimeFormData(prev => ({ ...prev, start_time: e.target.value }))}
                      className="w-full h-9 px-3 text-[13px] border border-gray-300 rounded-[10px] focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all touch-manipulation"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">End Date</label>
                    <input
                      type="date"
                      value={timeFormData.end_date}
                      onChange={(e) => setTimeFormData(prev => ({ ...prev, end_date: e.target.value }))}
                      className="w-full h-9 px-3 text-[13px] border border-gray-300 rounded-[10px] focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all touch-manipulation"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">End Time</label>
                    <input
                      type="time"
                      value={timeFormData.end_time}
                      onChange={(e) => setTimeFormData(prev => ({ ...prev, end_time: e.target.value }))}
                      className="w-full h-9 px-3 text-[13px] border border-gray-300 rounded-[10px] focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all touch-manipulation"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    />
                  </div>
                </div>
                <div className="flex justify-end">
                  <button
                    onClick={handleSaveTime}
                    className={`w-full ${isMobile ? 'px-4 py-3 text-[15px]' : 'px-4 py-2 text-sm'} font-semibold text-white bg-blue-600 rounded-[12px] hover:bg-blue-700 active:bg-blue-800 active:scale-[0.98] transition-all duration-150 touch-manipulation shadow-sm`}
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            ) : (
              <ScheduleTimeline
                booking={booking}
                isEditing={isEditingTime}
                onEdit={() => setIsEditingTime(true)}
                canEdit={canAccess.editBookingDetails && !booking.mobile_booking}
                isMobile={window.innerWidth < 640}
                onStatusChange={onStatusChange}
                readOnly={booking.status === 'cancelled'}
                onRescheduleMobileBooking={onRescheduleMobileBooking}
                onUpdateSchedule={async (data) => {
                  try {
                    if (onScheduleChange) {
                      // For mobile bookings, pass empty strings for drop_off fields
                      const dropOffDate = booking.mobile_booking ? '' : (booking.drop_off_date || '');
                      const dropOffTime = booking.mobile_booking ? '' : (booking.drop_off_time || '');
                      
                      await onScheduleChange(
                        dropOffDate,
                        dropOffTime,
                        data.startDate,
                        data.startTime,
                        data.endDate,
                        data.endTime
                      );
                    } else {
                      // Fallback to direct update
                      const updateData: any = {
                        start_date: data.startDate,
                        start_time: data.startTime,
                        end_date: data.endDate,
                        end_time: data.endTime
                      };
                      
                      // Only include drop_off fields for location bookings (not mobile)
                      if (!booking.mobile_booking) {
                        updateData.drop_off_date = booking.drop_off_date;
                        updateData.drop_off_time = booking.drop_off_time;
                      }
                      
                      await updateBooking(booking.id, updateData);
                      await onUpdate();
                    }
                  } catch (error) {
                    console.error('Error updating schedule:', error);
                    throw error;
                  }
                }}
              />
            )}
          </div>
        </div>

        {/* Job Timer Section: resource sees clock in/out + team; admin sees only who's on job */}
        {(currentResource?.id || isAdmin) && (
        <div className="bg-white rounded-[12px] border border-gray-200/60 overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
          {/* Part 1: Resource's own timer (clock in/out) — only for non-admin resource view */}
          {currentResource?.id && !isAdmin && (
            <div className={`${isMobile ? 'px-3 py-2.5' : 'px-6 py-4'} flex items-center justify-between border-b border-gray-100`}>
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${activeTimerEntry ? 'bg-red-50' : 'bg-gray-100'}`}>
                  <Clock className={`w-4 h-4 ${activeTimerEntry ? 'text-red-500' : 'text-gray-400'}`} />
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-900">Time on Job</span>
                  {activeTimerEntry ? (
                    <p className="text-xs font-mono text-red-600 mt-0.5">
                      {Math.floor(timerElapsed / 3600) > 0 && `${Math.floor(timerElapsed / 3600)}h `}
                      {Math.floor((timerElapsed % 3600) / 60)}m {timerElapsed % 60}s
                    </p>
                  ) : timerTotalCompleted > 0 ? (
                    <p className="text-xs text-gray-500 mt-0.5">
                      Total: {Math.floor(timerTotalCompleted / 3600) > 0 ? `${Math.floor(timerTotalCompleted / 3600)}h ` : ''}
                      {Math.floor((timerTotalCompleted % 3600) / 60)}m
                    </p>
                  ) : null}
                </div>
              </div>

              {activeTimerEntry ? (
                <button
                  onClick={() => stopTimer('manual')}
                  disabled={timerLoading}
                  className="flex items-center gap-1.5 px-4 py-1.5 bg-red-600 hover:bg-red-700 active:bg-red-800 text-white text-xs font-semibold rounded-full transition-all active:scale-[0.98] touch-manipulation shadow-sm disabled:opacity-50"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <Square className="w-3 h-3 fill-current" />
                  Stop
                </button>
              ) : (
                <button
                  onClick={handleStartTimerClick}
                  disabled={timerLoading}
                  className="flex items-center gap-1.5 px-4 py-1.5 bg-green-600 hover:bg-green-700 active:bg-green-800 text-white text-xs font-semibold rounded-full transition-all active:scale-[0.98] touch-manipulation shadow-sm disabled:opacity-50"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <Play className="w-3 h-3 fill-current" />
                  Start Timer
                </button>
              )}
            </div>
          )}

          {/* Part 2: Team / Resources working on job (only when there are others clocked in, or admin view) */}
          {(() => {
            const teamList = activeTimers.filter((t) => isAdmin || t.resource_id !== currentResource?.id);
            if (teamList.length === 0 && !(isAdmin && activeTimers.length === 0)) return null;
            return (
            <div className={`${isMobile ? 'px-3 py-2.5' : 'px-6 py-4'} ${currentResource?.id && !isAdmin ? 'border-t border-gray-100' : ''}`}>
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                {isAdmin ? 'Resources working on job' : 'Team working on job'}
              </h3>
              {teamList.length > 0 ? (
              <ul className="space-y-2">
                {teamList.map((t) => {
                    const name = [t.resource?.first_name, t.resource?.last_name].filter(Boolean).join(' ').trim() || 'Resource';
                    const elapsed = getElapsedSeconds(t.started_at);
                    return (
                      <li key={t.id} className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          {t.resource?.avatar_url ? (
                            <img src={t.resource.avatar_url} alt="" className="w-6 h-6 rounded-full object-cover flex-shrink-0" />
                          ) : (
                            <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                              <span className="text-[10px] font-medium text-gray-600">
                                {(t.resource?.first_name?.[0] || '') + (t.resource?.last_name?.[0] || '')}
                              </span>
                            </div>
                          )}
                          <span className="text-sm font-medium text-gray-900 truncate">{name}</span>
                        </div>
                        <span className="text-xs font-mono text-gray-500 flex-shrink-0">
                          {Math.floor(elapsed / 3600) > 0 && `${Math.floor(elapsed / 3600)}h `}
                          {Math.floor((elapsed % 3600) / 60)}m {elapsed % 60}s
                        </span>
                      </li>
                    );
                  })}
              </ul>
              ) : isAdmin ? (
                <p className="text-sm text-gray-400">No one clocked in</p>
              ) : null}
            </div>
            );
          })()}

          {/* View all time logs */}
          <div className={`${isMobile ? 'px-3 py-2' : 'px-6 py-3'} border-t border-gray-100`}>
            <button
              type="button"
              onClick={() => setIsJobTimeLogsModalOpen(true)}
              className="flex items-center gap-2 text-xs font-medium text-gray-600 hover:text-gray-900 transition-colors"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <FileText className="w-3.5 h-3.5" />
              View all time logs
            </button>
          </div>
        </div>
        )}

        {/* Job Time Logs Modal */}
        {isJobTimeLogsModalOpen && (
          <JobTimeLogsModal
            bookingId={booking.id}
            onClose={() => setIsJobTimeLogsModalOpen(false)}
            isAdmin={isAdmin}
          />
        )}

        {/* Clock-in required before starting job timer (hourly resources) */}
        {showClockInPromptModal && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4" onClick={() => !clockInPromptLoading && setShowClockInPromptModal(false)}>
            <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900">You're not clocked in</h3>
                  <p className="text-sm text-gray-500 mt-0.5">Are you working? Clock in to start the job timer.</p>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => !clockInPromptLoading && setShowClockInPromptModal(false)}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleClockInAndStartTimer}
                  disabled={clockInPromptLoading}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors disabled:opacity-50"
                >
                  {clockInPromptLoading ? 'Clocking in…' : 'Clock in & start timer'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Customer Feedback Section - Show for in_progress and completed bookings */}
        {(booking.status === 'completed' || booking.status === 'in_progress') && (
          <div className="bg-white rounded-[12px] border border-gray-200/60 overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
            <div className={`${isMobile ? 'px-3 py-2.5' : 'px-6 py-4'} border-b border-gray-200/60 bg-gray-50/50`}>
              <div className="flex items-center justify-between">
                <h3 className="text-xs sm:text-base font-bold text-gray-900 flex items-center">
                  <CheckCircle className="h-3 w-3 sm:h-5 sm:w-5 mr-1.5 sm:mr-2 text-green-600" />
                  Customer Feedback
                </h3>
                {bookingFeedback && canAccess.editBookingDetails && (
                  <div className="flex items-center space-x-2">
                    {isEditingFeedback ? (
                      <>
                        <button
                          onClick={handleSaveFeedback}
                          disabled={isSavingFeedback}
                          className="flex items-center px-2 py-1 text-xs font-medium text-white bg-green-600 rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          <Save className="h-3 w-3 mr-1" />
                          {isSavingFeedback ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          onClick={handleCancelEditingFeedback}
                          disabled={isSavingFeedback}
                          className="flex items-center px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          <XCircle className="h-3 w-3 mr-1" />
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={handleStartEditingFeedback}
                        className="flex items-center px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
                      >
                        <Edit3 className="h-3 w-3 mr-1" />
                        Edit Ratings
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="p-2 sm:p-6">
              {!isFeedbackEnabledForBusiness ? (
                // Show disabled message when feedback is turned off
                <div className="text-center py-4">
                  <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded-md border">
                    Customer Feedback is disabled
                  </div>
                </div>
              ) : bookingFeedback ? (
                // Show feedback results when available and enabled
                <div className="space-y-3">
                  <div className="text-xs text-gray-600">
                    Submitted {format(new Date(bookingFeedback.submitted_at), 'MMM d, yyyy')}
                  </div>

                  {/* Dynamic Overall Rating Calculation */}
                  {(() => {
                    // Get responses from form_responses or fallback to legacy fields
                    const responses = (bookingFeedback as ExtendedBookingFeedback).form_responses || {};
                    const ratingFields = feedbackFormFields.filter(field => field.field_type === 'rating');
                    
                    let totalRating = 0;
                    let ratingCount = 0;
                    
                    // Calculate overall rating from dynamic responses
                    if (Object.keys(responses).length > 0) {
                      ratingFields.forEach(field => {
                        const value = responses[field.field_key];
                        if (typeof value === 'number' && value > 0) {
                          totalRating += value;
                          ratingCount++;
                        }
                      });
                    } else {
                      // Fallback to legacy fields for backward compatibility
                      if (bookingFeedback.overall_quality_rating) {
                        totalRating += bookingFeedback.overall_quality_rating;
                        ratingCount++;
                      }
                      if (bookingFeedback.overall_experience_rating) {
                        totalRating += bookingFeedback.overall_experience_rating;
                        ratingCount++;
                      }
                    }
                    
                    const overallRating = ratingCount > 0 ? totalRating / ratingCount : 0;

                    return (
                      /* Overall Rating - Compact Single Line with Dropdown */
                      <div className="bg-gray-50 rounded-md p-2">
                        <button
                          onClick={() => setShowIndividualRatings(!showIndividualRatings)}
                          className="flex items-center justify-between w-full text-left hover:bg-gray-100 rounded transition-colors"
                        >
                          <div className="text-xs font-medium text-gray-700">Overall Rating</div>
                          <div className="flex items-center space-x-2">
                            <StarRating rating={Math.round(overallRating)} size="small" />
                            <span className="text-sm font-bold text-gray-900">
                              {overallRating.toFixed(1)}
                            </span>
                            {showIndividualRatings ? (
                              <ChevronDown className="h-3 w-3 text-gray-600" />
                            ) : (
                              <ChevronRight className="h-3 w-3 text-gray-600" />
                            )}
                          </div>
                        </button>
                        
                        {/* Individual Ratings - Dynamic Dropdown Content */}
                        {showIndividualRatings && (
                          <div className="mt-2 pt-2 border-t border-gray-200 space-y-2">
                            {/* Show dynamic rating fields */}
                            {Object.keys(responses).length > 0 ? (
                              ratingFields.map(field => {
                                const value = responses[field.field_key];
                                if (typeof value === 'number' && value > 0) {
                                  const currentRating = isEditingFeedback 
                                    ? (editingFeedbackData.form_responses?.[field.field_key] as number) || value
                                    : value;
                                  
                                  return (
                                    <div key={field.id} className="flex items-center justify-between py-1.5 px-2 bg-white rounded text-xs">
                                      <span className="text-gray-700">{field.field_label}</span>
                                      {isEditingFeedback ? (
                                        <EditableStarRating
                                          rating={currentRating}
                                          onRatingChange={(newRating) => handleRatingChange(field.field_key, newRating)}
                                          size="small"
                                        />
                                      ) : (
                                        <div className="flex items-center space-x-1">
                                          <StarRating rating={value} size="small" />
                                          <span className="text-gray-600 w-6">{value}/5</span>
                                        </div>
                                      )}
                                    </div>
                                  );
                                }
                                return null;
                              })
                            ) : (
                              // Fallback to legacy display for old feedback
                              <>
                                {bookingFeedback.overall_quality_rating && (
                                  <div className="flex items-center justify-between py-1.5 px-2 bg-white rounded text-xs">
                                    <span className="text-gray-700">Quality</span>
                                    {isEditingFeedback ? (
                                      <EditableStarRating
                                        rating={editingFeedbackData.overall_quality_rating || bookingFeedback.overall_quality_rating}
                                        onRatingChange={(newRating) => handleRatingChange('overall_quality_rating', newRating)}
                                        size="small"
                                      />
                                    ) : (
                                      <div className="flex items-center space-x-1">
                                        <StarRating rating={bookingFeedback.overall_quality_rating} size="small" />
                                        <span className="text-gray-600 w-6">{bookingFeedback.overall_quality_rating}/5</span>
                                      </div>
                                    )}
                                  </div>
                                )}
                                {bookingFeedback.overall_experience_rating && (
                                  <div className="flex items-center justify-between py-1.5 px-2 bg-white rounded text-xs">
                                    <span className="text-gray-700">Experience</span>
                                    {isEditingFeedback ? (
                                      <EditableStarRating
                                        rating={editingFeedbackData.overall_experience_rating || bookingFeedback.overall_experience_rating}
                                        onRatingChange={(newRating) => handleRatingChange('overall_experience_rating', newRating)}
                                        size="small"
                                      />
                                    ) : (
                                      <div className="flex items-center space-x-1">
                                        <StarRating rating={bookingFeedback.overall_experience_rating} size="small" />
                                        <span className="text-gray-600 w-6">{bookingFeedback.overall_experience_rating}/5</span>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Dynamic Text Responses */}
                  {(() => {
                    const responses = (bookingFeedback as ExtendedBookingFeedback).form_responses || {};
                    const textFields = feedbackFormFields.filter(field => field.field_type === 'text');
                    
                    // Show dynamic text responses
                    if (Object.keys(responses).length > 0) {
                      return textFields.map(field => {
                        const value = responses[field.field_key];
                        if (typeof value === 'string' && value.trim()) {
                          return (
                            <div key={field.id} className="space-y-1">
                              <div className="text-xs font-medium text-gray-600">{field.field_label}:</div>
                              <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded italic">
                                "{value}"
                              </div>
                            </div>
                          );
                        }
                        return null;
                      });
                    } else {
                      // Fallback to legacy improvement suggestions
                      if (bookingFeedback.improvement_suggestions) {
                        return (
                          <div className="space-y-1">
                            <div className="text-xs font-medium text-gray-600">Suggestions:</div>
                            <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded italic">
                              "{bookingFeedback.improvement_suggestions}"
                            </div>
                          </div>
                        );
                      }
                    }
                    return null;
                  })()}

                  {/* Feedback Link Section - Collapsible */}
                  {feedbackLink && (
                    <div className="border-t border-gray-200 pt-2">
                      <button
                        onClick={() => setShowFeedbackLink(!showFeedbackLink)}
                        className="flex items-center justify-between w-full text-left py-1 text-xs font-medium text-gray-600 hover:text-gray-800 transition-colors"
                      >
                        <span>Share link</span>
                        {showFeedbackLink ? (
                          <ChevronDown className="h-3 w-3" />
                        ) : (
                          <ChevronRight className="h-3 w-3" />
                        )}
                      </button>
                      
                      {showFeedbackLink && (
                        <div className="mt-1 space-y-1">
                          <div className="flex items-center space-x-1 p-2 bg-gray-50 rounded border text-xs">
                            <input
                              type="text"
                              value={feedbackLink}
                              readOnly
                              className="flex-1 bg-transparent border-none focus:outline-none text-gray-700"
                            />
                            <button
                              onClick={handleCopyFeedbackLink}
                              className="flex items-center px-2 py-1 font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                            >
                              {feedbackCopied ? (
                                <>
                                  <CheckCircle className="h-3 w-3 mr-1 text-green-600" />
                                  Copied
                                </>
                              ) : (
                                <>
                                  <Copy className="h-3 w-3 mr-1" />
                                  Copy
                                </>
                              )}
                            </button>
                            <a
                              href={feedbackLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center px-2 py-1 font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 transition-colors"
                            >
                              <ExternalLink className="h-3 w-3 mr-1" />
                              Preview
                            </a>
                          </div>
                          <p className="text-xs text-gray-500">
                            💡 Use <code className="bg-gray-200 px-1 rounded">[[feedback_link]]</code> in SMS automations
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                // Show no feedback message and link generation when enabled but no feedback yet
                <div className="space-y-2">
                  <p className="text-xs text-gray-600">No feedback yet.</p>
                  
                  {/* Show feedback link for sharing - Collapsible */}
                  {feedbackLink && (
                    <div>
                      <button
                        onClick={() => setShowFeedbackLink(!showFeedbackLink)}
                        className="flex items-center justify-between w-full text-left py-1 text-xs font-medium text-gray-600 hover:text-gray-800 transition-colors border-t border-gray-200"
                      >
                        <span>Share link to collect feedback</span>
                        {showFeedbackLink ? (
                          <ChevronDown className="h-3 w-3" />
                        ) : (
                          <ChevronRight className="h-3 w-3" />
                        )}
                      </button>
                      
                      {showFeedbackLink && (
                        <div className="mt-1 space-y-1">
                          <div className="flex items-center space-x-1 p-2 bg-gray-50 rounded border text-xs">
                            <input
                              type="text"
                              value={feedbackLink}
                              readOnly
                              className="flex-1 bg-transparent border-none focus:outline-none text-gray-700"
                            />
                            <button
                              onClick={handleCopyFeedbackLink}
                              className="flex items-center px-2 py-1 font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                            >
                              {feedbackCopied ? (
                                <>
                                  <CheckCircle className="h-3 w-3 mr-1 text-green-600" />
                                  Copied
                                </>
                              ) : (
                                <>
                                  <Copy className="h-3 w-3 mr-1" />
                                  Copy
                                </>
                              )}
                            </button>
                            <a
                              href={feedbackLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center px-2 py-1 font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 transition-colors"
                            >
                              <ExternalLink className="h-3 w-3 mr-1" />
                              Preview
                            </a>
                          </div>
                          <p className="text-xs text-gray-500">
                            💡 Use <code className="bg-gray-200 px-1 rounded">[[feedback_link]]</code> in SMS automations
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Generate link button if no link exists and feedback is enabled */}
                  {!feedbackLink && (
                    <button
                      onClick={handleGenerateFeedbackLink}
                      disabled={isGeneratingFeedback}
                      className="w-full px-3 py-1.5 text-xs font-medium text-white bg-green-600 rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {isGeneratingFeedback ? 'Generating...' : 'Generate Feedback Link'}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}



        {/* Customer Info - iOS Style */}
        <div className="bg-white rounded-[12px] border border-gray-200/60 overflow-visible shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
          <div className={`${isMobile ? 'p-2' : 'p-2'}`}>
            <BookingCustomerInfo
              customer={{
                id: booking.customer?.id || '',
                first_name: booking.customer?.first_name || '',
                last_name: booking.customer?.last_name || '',
                email: booking.customer?.email || null,
                phone: booking.customer?.phone || '',
                business_id: booking.business_id,
                type: 'person',
                address: null,
                source: null,
                tags: [],
                last_interaction: null,
                lifetime_value: 0,
                next_service_date: null,
                referral_source: null,
                preferences: {},
                status: 'active',
                amount_spent: 0,
                credit_balance: 0,
                notes: null,
                tax_exempt: false,
                workflow_exempt: false,
                carfax_exempt: false,
                receive_reminders: true,
                receive_offers: true,
                lat: null,
                lng: null,
                is_supplier: false,
                repeat: false,
                created_at: booking.created_at,
                updated_at: booking.updated_at || booking.created_at,
                vehicles: booking.customer?.vehicles?.map(v => ({
                  ...v,
                  mileage: null,
                  notes: null,
                  last_service: null
                }))
              }}
              onEdit={canAccess.editBookingDetails ? onEditCustomer : undefined}
              onChangeCustomer={canAccess.editBookingDetails ? onChangeCustomer : undefined}
              bookingAddress={booking.booking_address || ''}
              onEditBookingAddress={canAccess.editBookingDetails ? onAddressUpdate : undefined}
              mobile_booking={booking.mobile_booking || false}
              canEdit={canAccess.editBookingDetails}
              customerNotes={booking.customer_notes}
              onEditCustomerNotes={canAccess.editBookingDetails ? handleSaveCustomerNotes : undefined}
              bookingSource={booking.source}
              onSourceUpdate={canAccess.editBookingDetails ? handleSourceUpdate : undefined}
              businessId={selectedBusiness?.id}
              bookingId={booking.id}
              onNavigateToAddress={booking.mobile_booking ? (address) => {
                const encoded = encodeURIComponent(address);
                window.open(`https://www.google.com/maps/dir/?api=1&destination=${encoded}`, '_blank');
              } : undefined}
              bookingLat={booking.booking_lat}
              bookingLng={booking.booking_lng}
            />
          </div>
        </div>

        {/* Referral Source Customer - Separate card below customer card */}
        {/* Only show if: customer exists, customer is new (not repeat), source is referral, and either has referral source or can edit */}
        {(() => {
          const hasCustomer = !!booking.customer;
          const isNewCustomer = booking.customer?.repeat === false;
          const sourceIsReferral = booking.source?.toLowerCase() === 'referral';
          const shouldShow = hasCustomer && isNewCustomer && sourceIsReferral && 
            (referralSourceCustomer || (canAccess.editBookingDetails && sourceIsReferral));
          
          return shouldShow;
        })() && (
          <div className="bg-white rounded-[12px] border border-gray-200/60 overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
            <div className="p-2">
              <div className="text-[10px] text-gray-500 mb-2">Referral Source</div>
              {loadingReferralSource ? (
                <div className="text-[12px] text-gray-500">Loading...</div>
              ) : referralSourceCustomer ? (
                <div className="flex items-center space-x-2.5">
                  <div className="h-6 w-6 rounded-full bg-purple-500 flex items-center justify-center text-white font-semibold text-[10px] flex-shrink-0">
                    {getPersonInitials(referralSourceCustomer.first_name, referralSourceCustomer.last_name)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[14px] font-semibold text-gray-900 truncate leading-tight">
                      {referralSourceCustomer.first_name} {referralSourceCustomer.last_name}
                    </div>
                    {referralSourceCustomer.email && (
                      <div className="flex items-center space-x-1.5 text-[12px] text-gray-600 mt-0.5">
                        <Mail className="h-3 w-3 text-gray-400" />
                        <span className="truncate">{referralSourceCustomer.email}</span>
                      </div>
                    )}
                  </div>
                  {canAccess.editBookingDetails && (
                    <button
                      onClick={() => setIsSelectReferralSourceModalOpen(true)}
                      className="px-3 py-1.5 text-xs font-semibold text-blue-600 bg-blue-50 hover:bg-blue-100 active:bg-blue-200 rounded-xl transition-all duration-150 active:scale-[0.98] touch-manipulation flex-shrink-0"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      Change
                    </button>
                  )}
                </div>
              ) : canAccess.editBookingDetails ? (
                <button
                  onClick={() => setIsSelectReferralSourceModalOpen(true)}
                  className="w-full flex items-center justify-center space-x-1.5 text-[12px] text-blue-600 bg-blue-50 border border-blue-200 rounded-[8px] py-2 hover:bg-blue-100 active:scale-95 transition-all touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <Users className="h-3.5 w-3.5" />
                  <span>Select Referral Source</span>
                </button>
              ) : null}
            </div>
          </div>
        )}

        {/* Proposals Section */}
        {bookingProposals && bookingProposals.length > 0 && (
          <div className="space-y-2">
            {bookingProposals.map((proposal: any) => {
              const getDisplayStatus = () => {
                if (proposal.status === 'approved' || proposal.status === 'booked') return proposal.status;
                if (proposal.valid_until && new Date(proposal.valid_until) < new Date()) return 'expired';
                return proposal.status;
              };
              const displayStatus = getDisplayStatus();
              const isExpired = displayStatus === 'expired';

              // Calculate correct display total
              const calcProposalDisplayTotal = (): number => {
                if (proposal.status === 'approved' || proposal.status === 'booked') {
                  const approvedOffer = proposal.offers?.find((o: any) => o.status === 'approved');
                  if (approvedOffer) {
                    const svcs = (approvedOffer.services || []).filter((s: any) => {
                      const sel = s.isSelectable || s.is_selectable;
                      return !sel || (sel && s.is_selected);
                    });
                    const prods = (approvedOffer.products || []).filter((p: any) => {
                      const sel = p.isSelectable || p.is_selectable;
                      return !sel || (sel && p.is_selected);
                    });
                    const sub = svcs.reduce((s: number, x: any) => s + (x.price || 0), 0)
                      + prods.reduce((s: number, x: any) => s + ((x.price || 0) * (x.quantity || 1)), 0);
                    const disc = svcs.reduce((s: number, x: any) => s + (x.discount_amount || 0), 0)
                      + prods.reduce((s: number, x: any) => s + (x.discount_amount || 0), 0);
                    const discSub = sub - disc;
                    const tax = svcs.reduce((s: number, x: any) => s + (x.tax_exempt ? 0 : ((x.price || 0) - (x.discount_amount || 0)) * ((x.tax_rate || 0) / 100)), 0)
                      + prods.reduce((s: number, x: any) => s + (x.tax_exempt ? 0 : (((x.price || 0) * (x.quantity || 1)) - (x.discount_amount || 0)) * ((x.tax_rate || 0) / 100)), 0);
                    return Math.round((discSub + tax) * 100) / 100;
                  }
                }
                if (proposal.offers && proposal.offers.length > 0) {
                  return Math.max(...proposal.offers.map((offer: any) => {
                    const svcs = offer.services || [];
                    const prods = offer.products || [];
                    const sub = svcs.reduce((s: number, x: any) => s + (x.price || 0), 0)
                      + prods.reduce((s: number, x: any) => s + ((x.price || 0) * (x.quantity || 1)), 0);
                    const disc = svcs.reduce((s: number, x: any) => s + (x.discount_amount || 0), 0)
                      + prods.reduce((s: number, x: any) => s + (x.discount_amount || 0), 0);
                    const discSub = sub - disc;
                    const tax = svcs.reduce((s: number, x: any) => s + (x.tax_exempt ? 0 : ((x.price || 0) - (x.discount_amount || 0)) * ((x.tax_rate || 0) / 100)), 0)
                      + prods.reduce((s: number, x: any) => s + (x.tax_exempt ? 0 : (((x.price || 0) * (x.quantity || 1)) - (x.discount_amount || 0)) * ((x.tax_rate || 0) / 100)), 0);
                    return Math.round((discSub + tax) * 100) / 100;
                  }));
                }
                return proposal.total_amount || 0;
              };
              const proposalDisplayTotal = calcProposalDisplayTotal();
              const isMaxPotential = (proposal.status !== 'approved' && proposal.status !== 'booked') && (proposal.offers?.length || 0) > 1;

              const getStatusBadgeColor = (status: string) => {
                switch (status) {
                  case 'approved': return 'bg-green-50 text-green-700';
                  case 'sent': return 'bg-blue-50 text-blue-700';
                  case 'rejected': case 'declined': return 'bg-red-50 text-red-700';
                  case 'expired': return 'bg-gray-100 text-gray-600';
                  case 'draft': return 'bg-gray-100 text-gray-600';
                  default: return 'bg-gray-100 text-gray-600';
                }
              };

              const formatDate = (dateString: string) => {
                return new Date(dateString).toLocaleDateString('en-US', {
                  month: 'short', day: 'numeric', year: 'numeric'
                });
              };

              // Compact single-row for booked proposals
              if (displayStatus === 'booked') {
                return (
                  <button
                    key={proposal.id}
                    type="button"
                    onClick={() => onViewProposal?.(proposal)}
                    className="w-full bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] px-4 py-2.5 flex items-center gap-3 active:bg-gray-50 transition-colors touch-manipulation"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <div className="w-7 h-7 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0">
                      <FileText className="w-3.5 h-3.5 text-green-600" />
                    </div>
                    <div className="flex-1 min-w-0 text-left">
                      <p className="text-xs font-semibold text-gray-900 truncate">{proposal.title}</p>
                    </div>
                    <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-green-50 text-green-700 flex-shrink-0">Booked</span>
                    <span className="text-xs font-bold text-gray-900 tabular-nums flex-shrink-0">${proposalDisplayTotal.toLocaleString()}</span>
                    <ChevronDown className="w-3.5 h-3.5 text-gray-300 -rotate-90 flex-shrink-0" />
                  </button>
                );
              }

              return (
                <div
                  key={proposal.id}
                  className="bg-white rounded-[12px] border border-gray-200/60 overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.05)]"
                >
                  <div className="p-2">
                    <div className={`flex flex-col transition-all duration-150 ${isExpired ? 'opacity-60' : ''}`}
                      style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif', WebkitTapHighlightColor: 'transparent' }}
                    >
                      <div className="flex-1">
                        <button
                          type="button"
                          onClick={() => onViewProposal?.(proposal)}
                          className="w-full active:scale-[0.98] transition-transform duration-150 cursor-pointer"
                        >
                          <div className="px-3 pt-3 pb-2 flex items-center justify-between">
                            <div className="flex items-center space-x-2.5 flex-1 min-w-0">
                              <div className="h-8 w-8 rounded-full bg-gradient-to-b from-gray-300 to-gray-400 shadow-sm flex items-center justify-center text-xs font-semibold text-white flex-shrink-0 uppercase">
                                {proposal.customer?.first_name?.[0] || ''}{proposal.customer?.last_name?.[0] || ''}
                              </div>
                              <div className="flex items-center space-x-2 flex-1 min-w-0">
                                <h3 className="text-xs font-semibold text-gray-900 truncate" style={{ letterSpacing: '-0.01em' }}>
                                  {proposal.customer?.first_name} {proposal.customer?.last_name}
                                </h3>
                                <span className={`px-1.5 py-0.5 text-[10px] font-semibold rounded-[4px] flex-shrink-0 ${getStatusBadgeColor(displayStatus)}`} style={{ letterSpacing: '-0.01em' }}>
                                  {displayStatus.charAt(0).toUpperCase() + displayStatus.slice(1)}
                                </span>
                              </div>
                            </div>
                            <div className="text-right flex-shrink-0 ml-3">
                              <p className="text-[10px] text-gray-500 mb-0.5" style={{ letterSpacing: '-0.01em' }}>
                                {isMaxPotential ? 'Max Potential' : 'Total'}
                              </p>
                              <p className="text-sm font-bold text-gray-900" style={{ letterSpacing: '-0.02em' }}>
                                ${proposalDisplayTotal.toLocaleString()}
                              </p>
                            </div>
                          </div>
                          <div className="px-3 pb-2">
                            <h2 className="text-xs font-semibold text-gray-900 truncate" style={{ letterSpacing: '-0.01em' }}>
                              {proposal.title}
                            </h2>
                          </div>
                          <div className="px-3 pb-3 flex items-center justify-between border-t border-gray-100 pt-2">
                            {proposal.valid_until && (
                              <div>
                                <p className="text-[10px] text-gray-500 mb-0.5" style={{ letterSpacing: '-0.01em' }}>Valid until</p>
                                <p className="text-[11px] font-medium text-gray-900" style={{ letterSpacing: '-0.01em' }}>
                                  {formatDate(proposal.valid_until)}
                                </p>
                              </div>
                            )}
                            <div className={`text-[10px] text-blue-600 font-semibold ${proposal.valid_until ? 'ml-auto' : ''}`}>
                              View full proposal →
                            </div>
                          </div>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Services Section — overflow-visible so "Add → Add Service" dropdown is not clipped (absolute menu sits outside card height) */}
        <div className="bg-white rounded-[12px] border border-gray-200/60 overflow-visible shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
          {invoiceLocked && (
            <div className="mx-2 mt-2 flex items-center gap-2 px-3 py-2 rounded-[10px] bg-gray-100 text-[12px] text-gray-600">
              <Lock className="h-3.5 w-3.5 shrink-0" />
              <span className="flex-1">
                Invoice{liveInvoice?.invoice_number ? ` #${liveInvoice.invoice_number}` : ''} is final — services, products, and discounts are locked.
              </span>
              {invoiceAmendable && canAccess.editBookingDetails && (
                <button
                  onClick={() => setShowAmendInvoice(true)}
                  className="shrink-0 text-[12px] font-semibold text-blue-600 px-2 py-0.5 rounded-md active:bg-blue-50 touch-manipulation"
                >
                  Amend
                </button>
              )}
            </div>
          )}
          <div className={`${isMobile ? 'p-2' : 'p-2'}`}>
            <BookingServiceList
            services={booking.services || []}
            onAddService={canEditBilling ? onAddService : undefined}
            onAddProposal={canEditBilling ? onAddProposal : undefined}
            onEditVehicle={canEditBilling ? onEditVehicle : undefined}
            onUpdate={onUpdate}
            onError={onError}
            customer={booking.customer}
            showSalesInfo={canAccess.bookingSalesInfo}
            canEdit={canEditBilling}
            bookingWorkflows={booking.booking_workflows || []}
            bookingStatus={booking.status}
            bookingId={booking.id}
            onAssignWorkflow={onAssignWorkflow}
          />
          </div>
        </div>

        {/* Products Section - Only show if Inventory app is installed and setting is enabled - iOS Style */}
        {isInventoryAppInstalled && showInventoryInBookings && (
          <div className="bg-white rounded-[12px] border border-gray-200/60 overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
            <BookingProductList
              bookingId={booking.id}
              onUpdate={onUpdate}
              onError={onError}
              showSalesInfo={canAccess.bookingSalesInfo}
              canEdit={canEditBilling}
            />
          </div>
        )}

        {/* Resources Section - iOS Style */}
        <div className="bg-white rounded-[12px] border border-gray-200/60 overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
          <BookingResourceList
            resources={booking.resources || []}
            onRemoveResource={canAccess.editBookingDetails ? onRemoveResource : undefined}
            onAddResource={canAccess.editBookingDetails ? onAddResource : undefined}
            assets={assetsEnabled ? (booking.booking_assets || []) : []}
            onRemoveAsset={canAccess.editBookingDetails && assetsEnabled ? onRemoveAsset : undefined}
            onAddAsset={canAccess.editBookingDetails && assetsEnabled ? onAddAsset : undefined}
          />
        </div>

        {/* Booking Documents Section — checklists / customer sign-offs tied to this booking */}
        {selectedBusiness?.id && (
          <BookingDocumentsSection businessId={selectedBusiness.id} bookingId={booking.id} />
        )}

        {/* Commission Section - Only shows if there are commission-based resources */}
        <BookingCommissionSection
          booking={booking}
          onUpdate={onUpdate}
          onError={onError}
        />

        {/* Payment Summary — desktop only (mobile uses sticky footer at modal level) */}
        {!isMobile && canAccess.bookingSalesInfo && (
          <div className="bg-white rounded-[12px] border border-gray-200/60 overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
            <BookingPaymentSummary
              subtotal={booking.subtotal || 0}
              discountAmount={booking.discount_amount}
              discounts={booking.discounts as any}
              taxAmount={booking.tax_amount || 0}
              totalAmount={booking.total_amount || 0}
              payments={booking.payments as any}
              lineItems={booking.services as any}
              businessId={booking.business_id || ''}
              bookingId={booking.id}
              onApplyDiscount={onApplyDiscount}
              onDeleteDiscount={onDeleteDiscount}
              onRecordPayment={handleRecordPayment}
              onViewInvoice={() => setIsBetaPaymentsModalOpen(true)}
            />
          </div>
        )}

        <ApplyDiscountModal
          isOpen={isApplyDiscountModalOpen}
          onClose={() => setIsApplyDiscountModalOpen(false)}
          bookingId={booking.id}
          bookingTotal={booking.total_amount || 0}
          onApply={onUpdate}
        />

        <RecordPaymentModal
          isOpen={isRecordPaymentModalOpen}
          onClose={() => setIsRecordPaymentModalOpen(false)}
          onSuccess={handlePaymentsChanged}
          bookingId={booking.id}
          businessId={booking.business_id || ''}
          bookingTotal={liveInvoice ? liveInvoice.total_cents / 100 : (booking.total_amount || 0)}
          amountPaid={
            liveInvoice
              ? liveInvoice.amount_paid_cents / 100
              : booking.payments?.reduce(
                  (sum, payment) =>
                    isPaymentCountedForBalance(payment.status)
                      ? sum + paymentPrincipal(payment)
                      : sum,
                  0,
                ) || 0
          }
          customerId={booking.customer?.id}
        />

        <PaymentDetailsModal
          isOpen={isPaymentDetailsModalOpen}
          onClose={() => {
            setIsPaymentDetailsModalOpen(false);
            setSelectedPayment(null);
          }}
          bookingId={booking.id}
          allPayments={allPaymentDetails}
          onUpdate={handlePaymentsChanged}
        />


        
        <BetaPaymentsModal
          isOpen={isBetaPaymentsModalOpen}
          onClose={() => setIsBetaPaymentsModalOpen(false)}
          bookingId={booking.id}
          bookingNumber={typeof booking.booking_number === 'number' ? booking.booking_number : parseInt(String(booking.booking_number)) || 0}
          bookingData={{
            total_amount: booking.total_amount || 0,
            customer: booking.customer ? {
              id: booking.customer.id,
              first_name: booking.customer.first_name || '',
              last_name: booking.customer.last_name || '',
              email: booking.customer.email || undefined,
              phone: booking.customer.phone || undefined,
              full_name: `${booking.customer.first_name || ''} ${booking.customer.last_name || ''}`.trim() || undefined
            } : undefined,
            payments: booking.payments?.map(p => ({
              id: p.id,
              amount: p.amount,
              tip_amount: (p as any).tip_amount || undefined,
              base_amount: (p as any).base_amount || undefined,
              surcharge_amount: (p as any).surcharge_amount || undefined,
              method: (p as any).method || undefined,
              payment_type: (p as any).payment_type || undefined,
              status: p.status,
              pdf_url: (p as any).pdf_url,
              created_at: p.created_at,
              reference: (p as any).reference || undefined,
              stripe_payment_intent_id: (p as any).stripe_payment_intent_id || undefined,
              stripe_checkout_session_id: (p as any).stripe_checkout_session_id || undefined,
              notes: (p as any).notes || undefined
            })) || []
          }}
          booking={booking}
          onUpdate={handlePaymentsChanged}
          isInvisible={isInvisiblePDFGeneration}
        />

        <SendInvoiceModal
          isOpen={isSendInvoiceModalOpen}
          onClose={() => setIsSendInvoiceModalOpen(false)}
          onSelectMethod={handleSendMethodSelect}
          customerEmail={booking.customer?.email || undefined}
          customerPhone={booking.customer?.phone || undefined}
          customerId={booking.customer?.id}
          hasStripeConnected={
            !!(selectedBusiness as any)?.stripe_connect_account_id &&
            (selectedBusiness as any)?.online_payments_enabled !== false
          }
          bookingTotal={liveInvoice ? liveInvoice.total_cents / 100 : (booking.total_amount || 0)}
          amountPaid={liveInvoice ? liveInvoice.amount_paid_cents / 100 : invoicePrincipalPaidForSendModal}
        />

        <SendLiabilityWaiverModal
          isOpen={isSendLiabilityWaiverModalOpen}
          onClose={() => setIsSendLiabilityWaiverModalOpen(false)}
          onSendSMS={handleSendLiabilityWaiverSMS}
          onSendEmail={handleSendLiabilityWaiverEmail}
          onSignOnDevice={handleSignLiabilityWaiverOnDevice}
          customerName={`${booking.customer?.first_name || ''} ${booking.customer?.last_name || ''}`.trim()}
          customerPhone={booking.customer?.phone}
          customerEmail={booking.customer?.email || undefined}
          isLoading={isSendingLiabilityWaiver}
        />

        {showAmendInvoice && selectedBusiness?.id && (
          <AmendInvoiceModal
            isOpen={showAmendInvoice}
            onClose={() => setShowAmendInvoice(false)}
            bookingId={booking.id}
            businessId={selectedBusiness.id}
            onAmended={() => { void handlePaymentsChanged(); }}
          />
        )}

        <VehicleDamageReportModal
          isOpen={isDamageReportModalOpen}
          onClose={() => setIsDamageReportModalOpen(false)}
          bookingId={booking.id}
          existingReport={damageReport}
          vehicleInfo={{
            make: booking.services?.[0]?.vehicle_make || undefined,
            model: booking.services?.[0]?.vehicle_model || undefined,
            year: undefined,
            color: undefined,
            licensePlate: undefined
          }}
          onSave={handleDamageReportSave}
        />


        {/* Project Info Modal - Show linked project details */}
        {linkedProject && linkedProject.id && isProjectInfoModalOpen && (
          <ProjectInfoModal
            isOpen={isProjectInfoModalOpen}
            onClose={() => {
              console.log('🔄 Closing ProjectInfoModal');
              setIsProjectInfoModalOpen(false);
            }}
            project={linkedProject}
            onUpdate={async () => {
              // Reload project data after update
              if (booking?.project_id && supabase) {
                const { data: projectData } = await supabase
                  .from('projects')
                  .select('*')
                  .eq('id', booking.project_id)
                  .single();
                if (projectData) {
                  setLinkedProject(projectData);
                }
              }
              await onUpdate();
            }}
            navigate={navigate}
          />
        )}

        {/* Portal Toast for Modal Context */}
        {showPortalToast && (
          <PortalToast
            type={portalToastType}
            onClose={() => setShowPortalToast(false)}
          >
            {portalToastMessage}
          </PortalToast>
        )}

        {/* Select Referral Source Customer Modal */}
        <SelectCustomerModal
          isOpen={isSelectReferralSourceModalOpen}
          onClose={() => setIsSelectReferralSourceModalOpen(false)}
          onSelect={async (selectedCustomer) => {
            await handleReferralSourceUpdate(selectedCustomer.id);
            setIsSelectReferralSourceModalOpen(false);
          }}
        />
      </div>
    );
  }

  if (activeTab === 'materials') {
    return (
      <BookingMaterialsTab
        materials={booking.materials || []}
        isInventoryAppInstalled={isInventoryAppInstalled}
        isExpensesAppInstalled={isExpensesAppInstalled}
        bookingId={booking.id}
        businessId={booking.business_id}
        onAddMaterial={async (item, quantity, source = 'materials') => {
          try {
            if (source === 'inventory') {
              // Handle inventory item
              const stockItem = item as any; // StockItem type
              
              // 1. Create stock transaction to withdraw from inventory
              const { createStockTransaction } = await import('../../lib/stockItems');
              await createStockTransaction({
                item_id: stockItem.id,
                type: 'sale',
                quantity: quantity,
                price: stockItem.acquisition_cost,
                notes: `Added as material to booking: ${booking.id}`,
                booking_id: booking.id
              });

              // 2. Insert booking material using RPC function
              const { data: insertedId, error: addError } = await supabase!
                .rpc('insert_booking_material', {
                  p_booking_id: booking.id,
                  p_project_id: booking.project_id || null,
                  p_material_id: null,
                  p_item_id: stockItem.id,
                  p_material_name: stockItem.name,
                  p_material_description: stockItem.description || null,
                  p_quantity: quantity,
                  p_unit: stockItem.unit,
                  p_cost: stockItem.acquisition_cost,
                  p_total_cost: stockItem.acquisition_cost * quantity
                });

              if (addError) throw addError;
            } else {
              // Handle regular material
              const material = item as any; // Material type
              
              // Insert booking material using RPC function
              const { data: insertedId, error: addError } = await supabase!
                .rpc('insert_booking_material', {
                  p_booking_id: booking.id,
                  p_project_id: booking.project_id || null,
                  p_material_id: material.id,
                  p_item_id: null,
                  p_material_name: material.name,
                  p_material_description: material.description || null,
                  p_quantity: quantity,
                  p_unit: material.unit,
                  p_cost: material.cost,
                  p_total_cost: material.cost * quantity
                });

              if (addError) throw addError;
            }

            await onUpdate();
          } catch (err) {
            console.error('Error adding material:', err);
            onError(err instanceof Error ? err.message : 'Failed to add material');
          }
        }}
        onRemoveMaterial={async (id) => {
          try {
            // 1. Fetch the booking_material to check if it has item_id (inventory item)
            const { data: material, error: fetchError } = await supabase!
              .from('booking_materials')
              .select('id, item_id, quantity')
              .eq('id', id)
              .single();

            if (fetchError) throw fetchError;

            // 2. If it's an inventory item (item_id is not null), restore stock
            if (material.item_id) {
              // Find and delete the stock transaction for this booking and item
              const { error: transactionError } = await supabase!
                .from('stock_transactions')
                .delete()
                .eq('booking_id', booking.id)
                .eq('item_id', material.item_id)
                .eq('type', 'sale');

              // The deletion trigger will automatically restore stock quantity
              if (transactionError) {
                console.error('Error removing stock transaction:', transactionError);
                // Continue with material deletion even if transaction deletion fails
                // but show a warning
                onError('Material removed but stock may not have been restored. Please check inventory.');
              }
            }

            // 3. Delete the booking_material entry
            const { error: deleteError } = await supabase!
              .from('booking_materials')
              .delete()
              .eq('id', id);

            if (deleteError) throw deleteError;

            await onUpdate();
          } catch (err) {
            console.error('Error removing material:', err);
            onError(err instanceof Error ? err.message : 'Failed to remove material');
          }
        }}
        onUpdate={onUpdate}
        onError={onError}
        onOpenMaterialModal={() => setIsSelectMaterialModalOpen(true)}
        booking={booking}
      />
    );
  }

  if (activeTab === 'flat-rates') {
    return (
      <BookingFlatRatesTab
        bookingId={booking.id}
        businessId={booking.business_id}
        onUpdate={onUpdate}
        onError={onError}
        booking={booking}
      />
    );
  }

  if (activeTab === 'profit-loss') {
    return (
      <BookingProfitLoss
        revenue={booking.subtotal || 0}
        materialCosts={booking.materials?.reduce((sum, material) => 
          sum + (material.total_cost || 0), 0) || 0}
        discounts={booking.discounts?.reduce((sum, discount) => {
          if (discount.type === 'percentage') {
            return sum + (booking.subtotal * discount.amount / 100);
          }
          return sum + discount.amount;
        }, 0) || 0}
        laborCosts={booking.flat_rates?.reduce((sum, rate) => {
          let rateAmount = 0;
          if ((rate as any).resource_flat_rate_amount !== null && (rate as any).resource_flat_rate_amount !== undefined) {
            rateAmount = Number((rate as any).resource_flat_rate_amount);
          } else if ((rate as any).resource_hourly_rate !== null && (rate as any).resource_hourly_rate !== undefined) {
            rateAmount = Number((rate as any).resource_hourly_rate);
          }
          
          return sum + (rate.hours * rateAmount);
        }, 0) || 0}
        bookingId={booking.id}
        flatRates={booking.flat_rates || []}
      />
    );
  }

  return null;
}